import React, { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RotateCcw, Play, Loader2, AlertTriangle, CheckCircle2, Clock, Search, ChevronLeft, ChevronRight, Zap, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { alibabaApi, getBrokenChainStats, type BrokenChainStats, type PipelineOverviewItem, type KeywordMatrixRow, type UrlDetailItem, type AliStepDetailResponse, type AliSearchProgressRow, type AliCompanyProgressRow, type PdProductRow, type AliPdMatrixRow, type CoCompanyRow, type CoNavCategory } from '@/api/alibaba';
import { api } from '@/api/client';
import { serpApi } from '@/api/serp';
import { serpAnalysisApi, type AnalysisListItem, type StepsResponse } from '@/api/serp-analysis';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { projectsApi } from '@/api/projects';
import { keywordGroupsApi } from '@/api/keyword-groups';
import { PipelineTimeline } from '@/components/pipeline/PipelineTimeline';

// ── Matrix column definitions ──
type MatrixCol = { topKey: keyof KeywordMatrixRow; nonKey: keyof KeywordMatrixRow; label: string; sub: string; desc: string; mode: 'serp' | 'ali' }
const MATRIX_COLS: MatrixCol[] = [
  // SERP universal (source: scraped_content, 7000+ records)
  { topKey: 'a1_top', nonKey: 'a1_non', label: 'A-1', sub: 'QS HTML',     desc: 'A-1 QS 靜態 HTML｜scraped_content 有紀錄即視為完成',              mode: 'serp' },
  { topKey: 'a2_top', nonKey: 'a2_non', label: 'A-2', sub: 'Schema',      desc: 'A-2 Schema.org 解析｜schema_markup_json 不為空',                  mode: 'serp' },
  { topKey: 'a3_top', nonKey: 'a3_non', label: 'A-3', sub: '100 imgs',    desc: 'A-3 產品圖片｜images_json 數量 ≥ 100 張',                         mode: 'serp' },
  // Alibaba 産品頁 pipeline (source: pipeline_steps; DB step_ids still A-4~C-5 pending rename → PD-1~PD-10)
  { topKey: 'a4_top', nonKey: 'a4_non', label: 'PD-1', sub: 'detailData',  desc: 'PD-1 QS detailData 解析｜解析 Alibaba 結構化商品資料（DB: A-4）',  mode: 'ali' },
  { topKey: 'b1_top', nonKey: 'b1_non', label: 'PD-2', sub: 'BA HTML',     desc: 'PD-2 BrowserAct 完整 JS HTML｜取得 7 個商品模組（DB: B-1）',       mode: 'ali' },
  { topKey: 'b2_top', nonKey: 'b2_non', label: 'PD-3', sub: 'Module',      desc: 'PD-3 Module 解析｜解析 BA HTML 中的 7 個商品模組（DB: B-2）',      mode: 'ali' },
  { topKey: 'b3_top', nonKey: 'b3_non', label: 'PD-4', sub: 'Attributes',  desc: 'PD-4 屬性提取｜從模組中提取商品規格屬性（DB: B-3）',              mode: 'ali' },
  { topKey: 'b4_top', nonKey: 'b4_non', label: 'PD-5', sub: 'Screenshot',  desc: 'PD-5 BA 截圖｜透過 BrowserAct 截取頁面截圖（DB: B-4）',           mode: 'ali' },
  { topKey: 'c1_top', nonKey: 'c1_non', label: 'PD-6', sub: 'iframe URL',  desc: 'PD-6 iframe URL 偵測｜從 HTML 找 iframe 網址（自動觸發）（DB: C-1）', mode: 'ali' },
  { topKey: 'c2_top', nonKey: 'c2_non', label: 'PD-7', sub: 'iframe page', desc: 'PD-7 iframe 完整頁面｜BrowserAct 取得 iframe HTML（DB: C-2）',     mode: 'ali' },
  { topKey: 'c3_top', nonKey: 'c3_non', label: 'PD-8', sub: 'iframe imgs', desc: 'PD-8 實拍圖提取｜從 iframe 頁面擷取産品實拍圖（DB: C-3）',        mode: 'ali' },
  { topKey: 'c4_top', nonKey: 'c4_non', label: 'PD-9', sub: 'iframe SS',   desc: 'PD-9 iframe 截圖｜對 iframe 頁面截圖（DB: C-4）',                 mode: 'ali' },
  { topKey: 'c5_top', nonKey: 'c5_non', label: 'PD-10', sub: 'SMI Upload', desc: 'PD-10 圖片上傳 ShareMyImage｜批次上傳實拍圖至 SMI CDN（DB: C-5）', mode: 'ali' },
];

// topDone/topTotal drives the color; nonDone/nonTotal shown in muted
function MatrixCell({ topDone, topTotal, nonDone, nonTotal, combined }: { topDone: number; topTotal: number; nonDone: number; nonTotal: number; combined?: boolean }) {
  if (combined) {
    // Ali mode: show total done / total (no TOP/非TOP split — Alibaba has its own ranking, not Google SERP)
    const total = topTotal + nonTotal;
    const done  = topDone  + nonDone;
    const pct   = total > 0 ? done / total : -1;
    const cls   = pct < 0 ? 'text-gray-300' : pct >= 0.8 ? 'text-green-600' : pct >= 0.5 ? 'text-yellow-600' : pct > 0 ? 'text-red-500' : 'text-gray-400';
    return (
      <span className="text-[11px] font-mono">
        <span className={cls}>{total === 0 ? '—' : `${done}/${total}`}</span>
      </span>
    );
  }
  const pct = topTotal > 0 ? topDone / topTotal : -1;
  const cls = pct < 0 ? 'text-gray-300' : pct >= 0.8 ? 'text-green-600' : pct >= 0.5 ? 'text-yellow-600' : pct > 0 ? 'text-red-500' : 'text-gray-400';
  const topStr = topTotal === 0 ? '—' : `${topDone}/${topTotal}`;
  const nonStr = nonTotal === 0 ? '—' : `${nonDone}/${nonTotal}`;
  return (
    <span className="text-[11px] font-mono">
      <span className={cls}>{topStr}</span>
      <span className="text-gray-300 mx-0.5">｜</span>
      <span className="text-gray-400">{nonStr}</span>
    </span>
  );
}

// A-4 SERP Analysis cell (per-keyword, not per-URL)
function SerpAnalysisCell({ item }: { item: AnalysisListItem | undefined }) {
  if (!item) return <span className="text-gray-300 text-[11px]">—</span>;
  if (!item.analysis_ready) {
    return (
      <div className="text-[10px] leading-tight">
        <div className="text-orange-500">⚠️ 補資料</div>
        <div className="text-gray-400 font-mono">已解析: {item.parsed_htags}/10</div>
      </div>
    );
  }
  if (item.steps_total === 0) {
    return <span className="text-[11px] text-blue-600 font-mono">✓ 待觸發</span>;
  }
  const done = item.steps_completed;
  const total = item.steps_total;
  if (item.status === 'completed') return <span className="text-[11px] text-green-600 font-mono font-bold">✅ {done}/{total}</span>;
  if (item.status === 'running')   return <span className="text-[11px] text-blue-600 font-mono">🔄 {done}/{total}</span>;
  if (item.status === 'stuck')     return <span className="text-[11px] text-yellow-600 font-mono">⚠ {done}/{total}</span>;
  if (item.status === 'failed')    return <span className="text-[11px] text-red-500 font-mono">✗ {done}/{total}</span>;
  return <span className="text-[11px] text-gray-400 font-mono">{done}/{total}</span>;
}

const LIMIT_OPTIONS = [50, 100, 200, 300, 1000] as const;

// ── PD 展開 Panel：PD-1~PD-10 步驟定義 ───────────────────────
const ALI_PD_STEPS = [
  { label: 'PD-1',  sub: 'QS',     dbId: 'A-4' },
  { label: 'PD-2',  sub: 'BA',     dbId: 'B-1' },
  { label: 'PD-3',  sub: '解析',   dbId: 'B-2' },
  { label: 'PD-4',  sub: '屬性',   dbId: 'B-3' },
  { label: 'PD-5',  sub: 'SS',     dbId: 'B-4' },
  { label: 'PD-6',  sub: 'iframe', dbId: 'C-1' },
  { label: 'PD-7',  sub: 'i頁面',  dbId: 'C-2' },
  { label: 'PD-8',  sub: '實拍圖', dbId: 'C-3' },
  { label: 'PD-9',  sub: 'iSS',    dbId: 'C-4' },
  { label: 'PD-10', sub: 'SMI',    dbId: 'C-5' },
] as const

function getPdStepStatus(item: PdProductRow, dbId: string): string {
  const ss = (item.stage_status ?? {}) as Record<string, string>
  switch (dbId) {
    case 'A-4': return item.task_id ? 'completed' : 'not_started'
    case 'B-1': return item.task_status ?? 'not_started'
    case 'B-2': return item.parse_status ?? (item.has_markdown ? 'completed' : 'not_started')
    case 'B-3': return ss['B-3'] ?? (item.parse_status === 'completed' ? 'completed' : 'not_started')
    case 'B-4': return item.has_screenshot ? 'completed' : 'not_started'
    case 'C-1': return item.has_iframe ? 'completed' : 'not_started'
    case 'C-2': return ss['C-2'] ?? 'not_started'
    case 'C-3': return (item.iframe_image_count ?? 0) > 0 ? 'completed' : 'not_started'
    case 'C-4': return ss['C-4'] ?? 'not_started'
    case 'C-5': return item.smi_done ? 'completed' : 'not_started'
    default:    return ss[dbId] ?? 'not_started'
  }
}

function PdStepIcon({ status }: { status: string }) {
  if (status === 'completed') return <span className="text-green-600">✅</span>
  if (status === 'failed')    return <span className="text-red-500">❌</span>
  if (status === 'running')   return <span className="text-blue-500">🔄</span>
  if (status === 'pending')   return <span className="text-yellow-500">⏳</span>
  return <span className="text-gray-300">○</span>
}

// ── 斷鏈修復進度面板 ─────────────────────────────────────────
function BrokenChainPanel() {
  const [stats, setStats] = useState<BrokenChainStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBrokenChainStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));

    // 每 60 秒自動刷新
    const timer = setInterval(() => {
      getBrokenChainStats().then(setStats).catch(console.error);
    }, 60_000);
    return () => clearInterval(timer);
  }, []);

  if (loading) return <div className="text-gray-400 text-xs p-2">載入斷鏈統計...</div>;
  if (!stats) return null;

  const repairRate = stats.total_fixed + stats.total_broken > 0
    ? Math.round(stats.total_fixed / (stats.total_fixed + stats.total_broken) * 100)
    : 0;
  const keywordRate = stats.keywords_total > 0
    ? Math.round(stats.keywords_ready / stats.keywords_total * 100)
    : 0;

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-orange-600 font-semibold text-sm">⚠️ 斷鏈修復進度</span>
        <span className="text-gray-400 text-xs">（每 60 秒更新）</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {/* 待修復 */}
        <div className="bg-white rounded p-2 border border-orange-100">
          <div className="text-[10px] text-gray-500 mb-1">待修復（斷鏈）</div>
          <div className="text-xl font-bold text-orange-600">{stats.total_broken.toLocaleString()}</div>
          <div className="text-[10px] text-gray-400">h_structure 缺失</div>
        </div>

        {/* 已修復 */}
        <div className="bg-white rounded p-2 border border-green-100">
          <div className="text-[10px] text-gray-500 mb-1">已修復</div>
          <div className="text-xl font-bold text-green-600">{stats.total_fixed.toLocaleString()}</div>
          <div className="text-[10px] text-gray-400">修復率 {repairRate}%</div>
        </div>

        {/* 最近 1 小時 */}
        <div className="bg-white rounded p-2 border border-blue-100">
          <div className="text-[10px] text-gray-500 mb-1">近 1 小時修復</div>
          <div className="text-xl font-bold text-blue-600">{stats.recent_fixed_1h.toLocaleString()}</div>
          <div className="text-[10px] text-gray-400">約 {Math.round(stats.recent_fixed_1h / 60)} 筆/分鐘</div>
        </div>

        {/* 關鍵字達標 */}
        <div className="bg-white rounded p-2 border border-purple-100">
          <div className="text-[10px] text-gray-500 mb-1">關鍵字達標（TOP10≥5）</div>
          <div className="text-xl font-bold text-purple-600">
            {stats.keywords_ready}/{stats.keywords_total}
          </div>
          <div className="text-[10px] text-gray-400">{keywordRate}% 可分析</div>
        </div>
      </div>

      {/* 各機器分解 */}
      {stats.by_machine.length > 0 && (
        <div>
          <div className="text-[10px] text-gray-500 mb-1">各機器待修任務：</div>
          <div className="flex flex-wrap gap-2">
            {stats.by_machine.map(m => (
              <span key={m.worker_id}
                className="text-[11px] bg-white border border-orange-200 rounded px-2 py-0.5 font-mono">
                {m.worker_id}: <span className="text-orange-600 font-bold">{m.broken_count.toLocaleString()}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 進度條 */}
      <div className="mt-3">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>整體修復進度</span>
          <span>{repairRate}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-green-500 h-1.5 rounded-full transition-all"
            style={{ width: `${repairRate}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function PipelineMonitor() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedTask, setExpandedTask] = useState<string | null>(null);

  // Matrix filters — mode synced with URL ?mode=
  const rawMode = searchParams.get('mode') ?? 'serp';
  const matrixMode = (['serp', 'ali', 'serp-llm', 'all'] as const).includes(rawMode as 'serp') ? rawMode as 'serp' | 'ali' | 'serp-llm' | 'all' : 'serp';
  const setMatrixMode = (m: 'serp' | 'ali' | 'serp-llm' | 'all') => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('mode', m); return p; }, { replace: true });
  };
  type SortKey = 'keyword' | 'completion' | 'a1' | 'a2' | 'a3';
  const [mSortBy,    setMSortBy]    = useState<SortKey>('completion');
  const [sortDir,    setSortDir]    = useState<'asc' | 'desc'>('desc');
  const [mProjectId, setMProjectId] = useState('');
  const [mGroupId,   setMGroupId]   = useState('');
  const [mTag,       setMTag]       = useState('');
  const [mSearch,    setMSearch]    = useState('');
  const [mLimit,     setMLimit]     = useState<50|100|200|300|1000>(50);
  const [mOffset,    setMOffset]    = useState(0);
  const [searchInput, setSearchInput] = useState('');

  // Cell detail modal state
  const [cellDetail, setCellDetail] = useState<{keyword: string; stage: 'a1'|'a2'|'a3'; label: string} | null>(null);

  // Hover tooltip state — SERP A1~A3
  const [hoverCell, setHoverCell] = useState<{keyword: string; stage: 'a1'|'a2'|'a3'; x: number; y: number} | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hover tooltip state — Alibaba A4~C5 (old pipeline_steps based)
  const [hoverAliCell, setHoverAliCell] = useState<{keyword: string; stepId: string; x: number; y: number} | null>(null);
  const hoverAliTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Hover tooltip state — PD tab (new, queries correct DB tables per step)
  const [hoverPdCell, setHoverPdCell] = useState<{keyword: string; pdStep: number; x: number; y: number} | null>(null);
  const hoverPdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tab3 Alibaba PD Panel — click-through panel showing full A-1~C-5 step status per URL
  const [aliPdPanel, setAliPdPanel] = useState<{ keyword: string } | null>(null);
  const [aliPdFilter, setAliPdFilter] = useState<'all' | 'failed' | 'pending' | 'not_started'>('all');
  const [aliPdOffset, setAliPdOffset] = useState(0);
  const ALI_PD_LIMIT = 50;

  // SC Drill-down panel state
  const [scDrilldown, setScDrilldown] = useState<{ keyword: string } | null>(null);
  const [scFilter, setScFilter] = useState<'all' | 'completed' | 'failed' | 'pending'>('all');
  // CO Drill-down panel state
  const [coDrilldown, setCoDrilldown] = useState<{ keyword: string } | null>(null);
  const [coFilter, setCoFilter] = useState<'all' | 'crawled' | 'pending' | 'failed'>('all');
  // SC batch state
  const [selectedScKeywords, setSelectedScKeywords] = useState<Set<string>>(new Set());
  const [isBatchScRetrying, setIsBatchScRetrying] = useState(false);
  // CO batch state
  const [selectedCoKeywords, setSelectedCoKeywords] = useState<Set<string>>(new Set());
  const [isBatchCoRetrying, setIsBatchCoRetrying] = useState(false);
  // PD step selection (0~9 = PD-1~PD-10)
  const [selectedPdSteps, setSelectedPdSteps] = useState<Set<number>>(new Set());
  // SC inline drilldown: selected pages
  const [selectedScPages, setSelectedScPages] = useState<Set<string>>(new Set());
  // CO inline drilldown: selected companies
  const [selectedCoCompanies, setSelectedCoCompanies] = useState<Set<string>>(new Set());

  // Tab2 A4 Step Panel — click keyword row in serp-llm mode to show Step1~7 progress
  const [selectedA4Keyword, setSelectedA4Keyword] = useState<{ keyword: string; requestId: string } | null>(null);
  // Track which step cards are expanded (to show output_markdown / output_json)
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  // Tab1 SERP Keyword Panel — click keyword row to show URL × A1/A2/A3 status
  const [selectedSerpKeyword, setSelectedSerpKeyword] = useState<string | null>(null);

  // Row × Column selection for immediate processing
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [selectedStages, setSelectedStages] = useState<Set<'a1'|'a2'|'a3'>>(new Set(['a1','a2','a3']));
  const [selectedAliStages, setSelectedAliStages] = useState<Set<string>>(new Set());
  const [isProcessingNow, setIsProcessingNow] = useState(false);
  const [isAliProcessingNow, setIsAliProcessingNow] = useState(false);
  const [isTriggeringSerpBatch, setIsTriggeringSerpBatch] = useState(false);
  const [isRetriggeringAli, setIsRetriggeringAli] = useState(false);
  // SC/CO per-keyword retry loading state
  const [scRetryingKws, setScRetryingKws] = useState<Set<string>>(new Set());
  const [coRetryingKws, setCoRetryingKws] = useState<Set<string>>(new Set());

  const [isIframeFetching, setIsIframeFetching] = useState(false);
  const [isBackfillingC4, setIsBackfillingC4] = useState(false);
  // Ali sub-tab: 'sc' | 'co' | 'pd' (only shown when matrixMode === 'ali')
  const [aliSubTab, setAliSubTab] = useState<'sc' | 'co' | 'pd'>('sc');

  const visibleCols = MATRIX_COLS.filter(c =>
    matrixMode === 'all' ? true :
    matrixMode === 'serp' ? c.mode === 'serp' :
    matrixMode === 'ali' ? c.mode === 'ali' :
    matrixMode === 'serp-llm' ? false :  // A-4 SERP only, no A1-C5
    c.mode === 'serp'
  );

  // SERP Analysis list — for A-4 SERP column in matrix
  const { data: serpAnalysisList = [] } = useQuery({
    queryKey: ['analysis-reports'],
    queryFn: async () => (await serpAnalysisApi.list({ limit: 1000 })).data?.items ?? [],
    refetchInterval: 30_000,
  });
  // keyword → latest AnalysisListItem
  const serpAnalysisMap = useMemo(() => {
    const m = new Map<string, AnalysisListItem>();
    for (const a of serpAnalysisList) {
      const ex = m.get(a.keyword);
      if (!ex || a.created_at > ex.created_at) m.set(a.keyword, a);
    }
    return m;
  }, [serpAnalysisList]);

  const { data: overview = [], isLoading, isError: overviewError } = useQuery({
    queryKey: ['pipeline-overview'],
    queryFn: async () => {
      const res = await alibabaApi.getPipelineOverview();
      return (res.data ?? []) as PipelineOverviewItem[];
    },
    refetchInterval: 30_000,
    retry: 2,
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => (await projectsApi.list()).data ?? [],
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['keyword-groups', mProjectId],
    queryFn: async () => mProjectId ? ((await keywordGroupsApi.list(mProjectId)).data ?? []) : [],
    enabled: !!mProjectId,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['keyword-tags', mProjectId],
    queryFn: async () => mProjectId ? ((await keywordGroupsApi.allTags(mProjectId)).data ?? []) : [],
    enabled: !!mProjectId,
  });

  const { data: matrixResult, isLoading: matrixLoading } = useQuery({
    queryKey: ['pipeline-keyword-matrix', mProjectId, mGroupId, mTag, mSearch, matrixMode, mSortBy, mLimit, mOffset],
    queryFn: async () => {
      const res = await alibabaApi.getKeywordMatrix({
        project_id:   mProjectId || undefined,
        group_id:     mGroupId   || undefined,
        tag:          mTag       || undefined,
        search:       mSearch    || undefined,
        // Alibaba mode: only count URLs matching alibaba.com so denominators are meaningful
        url_contains: matrixMode === 'ali' ? 'alibaba.com' : undefined,
        sort_by:      mSortBy,
        limit:        mLimit,
        offset:       mOffset,
      });
      return { rows: res.data ?? [], total: res.meta?.total ?? 0 };
    },
    refetchInterval: 60_000,
  });

  const matrixRows: KeywordMatrixRow[] = matrixResult?.rows ?? [];
  const matrixTotal = matrixResult?.total ?? 0;

  // URL detail query (for cell drill-down modal)
  const { data: detailItems = [], isLoading: detailLoading } = useQuery({
    queryKey: ['url-detail', cellDetail?.keyword, cellDetail?.stage],
    queryFn: async () => {
      if (!cellDetail) return [] as UrlDetailItem[];
      const res = await alibabaApi.getKeywordUrlDetail(cellDetail.keyword, cellDetail.stage, mProjectId || undefined);
      return res.data ?? [];
    },
    enabled: !!cellDetail,
  });

  // Ali 分類頁（路徑 A: alibaba_searches）進度
  const { data: aliSearchProgress = [] } = useQuery({
    queryKey: ['ali-search-progress', mProjectId],
    queryFn: async () => {
      const res = await alibabaApi.getAliSearchProgress(mProjectId || undefined);
      return (res.data ?? []) as AliSearchProgressRow[];
    },
    enabled: matrixMode === 'ali',
    refetchInterval: 30_000,
  });

  // Ali 公司頁（路徑 C: ali_company_profiles）進度
  const { data: aliCompanyProgressResult } = useQuery({
    queryKey: ['ali-company-progress', mProjectId],
    queryFn: async () => {
      const res = await alibabaApi.getAliCompanyProgress(mProjectId || undefined);
      // res.data = rows array; table_exists is at top-level of parsed JSON body
      return {
        data: res.data ?? [],
        table_exists: (res as unknown as { table_exists?: boolean }).table_exists ?? false
      };
    },
    enabled: matrixMode === 'ali',
    refetchInterval: 30_000,
  });
  const aliCompanyProgress: AliCompanyProgressRow[] = aliCompanyProgressResult?.data ?? [];

  // Hover tooltip query
  const { data: hoverItems = [], isLoading: hoverLoading } = useQuery({
    queryKey: ['hover-url-detail', hoverCell?.keyword, hoverCell?.stage],
    queryFn: async () => {
      if (!hoverCell) return [] as UrlDetailItem[];
      const res = await alibabaApi.getKeywordUrlDetail(hoverCell.keyword, hoverCell.stage, mProjectId || undefined);
      return res.data ?? [];
    },
    enabled: !!hoverCell,
    staleTime: 5 * 60 * 1000,
  });

  // Hover tooltip query — Alibaba A4~C5 (old, for non-PD mode)
  const { data: hoverAliData, isLoading: hoverAliLoading } = useQuery({
    queryKey: ['hover-ali-step-detail', hoverAliCell?.keyword, hoverAliCell?.stepId],
    queryFn: async () => {
      if (!hoverAliCell) return null;
      const res = await alibabaApi.getKeywordStepDetail(hoverAliCell.keyword, hoverAliCell.stepId, mProjectId || undefined);
      return res.data ?? null;
    },
    enabled: !!hoverAliCell,
    staleTime: 5 * 60 * 1000,
  });

  // Hover tooltip query — PD tab (correct source per PD-1~PD-10)
  const { data: hoverPdData, isLoading: hoverPdLoading } = useQuery({
    queryKey: ['hover-pd-step-tooltip', hoverPdCell?.keyword, hoverPdCell?.pdStep, mProjectId],
    queryFn: async () => {
      if (!hoverPdCell) return null;
      const res = await alibabaApi.getAliPdStepTooltip(hoverPdCell.keyword, hoverPdCell.pdStep, mProjectId || undefined);
      return res.data ?? null;
    },
    enabled: !!hoverPdCell,
    staleTime: 5 * 60 * 1000,
  });

  // Tab2 A4 Step Panel query — Step1~7 detail with 30s polling
  const { data: a4StepsData, isFetching: a4Fetching } = useQuery<StepsResponse | null>({
    queryKey: ['a4-steps', selectedA4Keyword?.requestId],
    queryFn: async () => {
      if (!selectedA4Keyword) return null
      const res = await serpAnalysisApi.getSteps(selectedA4Keyword.requestId)
      return res.data
    },
    enabled: !!selectedA4Keyword,
    refetchInterval: selectedA4Keyword ? 30000 : false,
  });

  // Tab3 AliPD Panel query — paginated A-1~C-5 steps for all URLs of a keyword
  const { data: aliPdData, isFetching: aliPdFetching } = useQuery({
    queryKey: ['ali-pd-panel', aliPdPanel?.keyword, aliPdOffset],
    queryFn: async () => {
      if (!aliPdPanel) return null
      const res = await alibabaApi.getKeywordAllSteps(aliPdPanel.keyword, mProjectId || undefined, ALI_PD_LIMIT, aliPdOffset)
      return res.data
    },
    enabled: !!aliPdPanel,
  });

  // SC Drill-down query
  const { data: scDrilldownData, isFetching: scDrilldownFetching } = useQuery({
    queryKey: ['ali-sc-pages', scDrilldown?.keyword, mProjectId],
    queryFn: async () => {
      if (!scDrilldown) return null
      const res = await alibabaApi.getAliScPages(scDrilldown.keyword, mProjectId || undefined)
      // res.data = ScPageRow[] array; wrap in object to match component usage of .data
      return { keyword: scDrilldown.keyword, data: res.data ?? [] }
    },
    enabled: !!scDrilldown,
  })

  // SC Suppliers stats query
  const { data: scSuppliersStatsData } = useQuery({
    queryKey: ['sc-suppliers-stats', scDrilldown?.keyword],
    queryFn: () => alibabaApi.getScSuppliersStats(scDrilldown!.keyword),
    enabled: !!scDrilldown?.keyword,
  })
  const scSuppliersStats = scSuppliersStatsData?.data?.data

  // PD Drill-down query (replaces aliPdData which uses wrong keyword-all-steps endpoint)
  const { data: aliPdProductsData, isFetching: aliPdProductsFetching } = useQuery({
    queryKey: ['ali-pd-products', aliPdPanel?.keyword, aliPdOffset, mProjectId],
    queryFn: async () => {
      if (!aliPdPanel) return null
      const res = await alibabaApi.getAliPdProducts(
        aliPdPanel.keyword, mProjectId || undefined, ALI_PD_LIMIT, aliPdOffset
      )
      const rows = res.data ?? []
      const total = res.meta?.total ?? rows.length
      return { total_products: total, data: rows }
    },
    enabled: !!aliPdPanel,
  })

  // Ali PD Keyword Matrix — Alibaba-native matrix (replaces keyword-matrix for PD tab)
  const { data: aliPdMatrixResult, isLoading: aliPdMatrixLoading } = useQuery({
    queryKey: ['ali-pd-keyword-matrix', mProjectId, mSearch, mSortBy, mLimit, mOffset],
    queryFn: async () => {
      const res = await alibabaApi.getAliPdMatrix({
        project_id: mProjectId || undefined,
        search:     mSearch    || undefined,
        sort_by:    mSortBy === 'keyword' ? 'keyword' : 'completion',
        limit:      mLimit,
        offset:     mOffset,
      })
      return { rows: res.data ?? [], total: res.meta?.total ?? 0 }
    },
    enabled: matrixMode === 'ali' && aliSubTab === 'pd',
    refetchInterval: 60_000,
  })
  const aliPdMatrixRows: AliPdMatrixRow[] = aliPdMatrixResult?.rows ?? []
  const aliPdMatrixTotal = aliPdMatrixResult?.total ?? 0

  // CO Drill-down query
  const { data: coDrilldownData, isFetching: coDrilldownFetching } = useQuery({
    queryKey: ['ali-co-companies', coDrilldown?.keyword, mProjectId],
    queryFn: async () => {
      if (!coDrilldown) return null
      const res = await alibabaApi.getAliCoCompanies(coDrilldown.keyword, mProjectId || undefined)
      // res.data = CoCompanyRow[]; table_exists is at top-level of parsed JSON body
      return {
        keyword: coDrilldown.keyword,
        table_exists: (res as unknown as { table_exists?: boolean }).table_exists ?? false,
        data: res.data ?? []
      }
    },
    enabled: !!coDrilldown,
  })

  // CO Nav categories — which company URL is expanded to show nav detail
  const [expandedNavUrl, setExpandedNavUrl] = useState<string | null>(null)
  const { data: navCategoriesData, isFetching: navCategoriesFetching } = useQuery({
    queryKey: ['ali-co-nav-categories', expandedNavUrl],
    queryFn: async () => {
      if (!expandedNavUrl) return []
      const res = await alibabaApi.getAliCoNavCategories(expandedNavUrl)
      return (res.data ?? []) as CoNavCategory[]
    },
    enabled: !!expandedNavUrl,
  })

  // Alibaba three-layer coverage overview
  const { data: aliOverviewData, refetch: refetchAliOverview } = useQuery({
    queryKey: ['alibaba-overview'],
    queryFn: () => alibabaApi.getAlibabaOverview(),
    enabled: matrixMode === 'ali',
    refetchInterval: 60_000,
  })
  const aliOverview = aliOverviewData?.data

  const triggerAliAction = async (action: 'trigger_co_crawl' | 'trigger_co_products' | 'trigger_ba_factory' | 'trigger_c4_backfill') => {
    try {
      await alibabaApi.triggerAlibabaAction(action, undefined, 5)
      toast.success(`${action} 已觸發`)
      refetchAliOverview()
    } catch {
      toast.error('觸發失敗')
    }
  }

  // Tab1 SERP Keyword Panel — parallel queries for A-1 / A-2 / A-3 URL detail
  const { data: serpPanelA1, isFetching: fetchingA1 } = useQuery({
    queryKey: ['serp-panel', selectedSerpKeyword, 'a1', mProjectId],
    queryFn: async () => {
      if (!selectedSerpKeyword) return null
      const res = await alibabaApi.getKeywordUrlDetail(selectedSerpKeyword, 'a1', mProjectId || undefined)
      return res.data
    },
    enabled: !!selectedSerpKeyword,
    refetchInterval: selectedSerpKeyword ? 30000 : false,
  });
  const { data: serpPanelA2 } = useQuery({
    queryKey: ['serp-panel', selectedSerpKeyword, 'a2', mProjectId],
    queryFn: async () => {
      if (!selectedSerpKeyword) return null
      const res = await alibabaApi.getKeywordUrlDetail(selectedSerpKeyword, 'a2', mProjectId || undefined)
      return res.data
    },
    enabled: !!selectedSerpKeyword,
    refetchInterval: selectedSerpKeyword ? 30000 : false,
  });
  const { data: serpPanelA3 } = useQuery({
    queryKey: ['serp-panel', selectedSerpKeyword, 'a3', mProjectId],
    queryFn: async () => {
      if (!selectedSerpKeyword) return null
      const res = await alibabaApi.getKeywordUrlDetail(selectedSerpKeyword, 'a3', mProjectId || undefined)
      return res.data
    },
    enabled: !!selectedSerpKeyword,
    refetchInterval: selectedSerpKeyword ? 30000 : false,
  });

  // Compute Alibaba pipeline completion rate for a row (10 stages × URL count)
  const getAliPct = (row: KeywordMatrixRow): number | null => {
    const total = (row.top_total + row.non_top_total) * 10;
    if (total === 0) return null;
    const done = ['a4','b1','b2','b3','b4','c1','c2','c3','c4','c5'].reduce((sum, s) => {
      return sum + ((row[`${s}_top` as keyof KeywordMatrixRow] as number ?? 0) + (row[`${s}_non` as keyof KeywordMatrixRow] as number ?? 0));
    }, 0);
    return Math.round(done / total * 100);
  };

  // Client-side sort — keyword (localeCompare), a1/a2/a3 (top+non / total), completion (completion_pct or aliPct)
  const sortedRows = useMemo(() => {
    if (!matrixRows.length) return matrixRows;
    if (mSortBy === 'keyword') {
      return [...matrixRows].sort((a, b) => sortDir === 'asc' ? a.keyword.localeCompare(b.keyword) : b.keyword.localeCompare(a.keyword));
    }
    const getScore = (row: KeywordMatrixRow) => {
      const total = row.top_total + row.non_top_total;
      if (total === 0) return -1;
      if (mSortBy === 'a1') return (row.a1_top + row.a1_non) / total;
      if (mSortBy === 'a2') return (row.a2_top + row.a2_non) / total;
      if (mSortBy === 'a3') return (row.a3_top + row.a3_non) / total;
      // 'completion': ali mode → Alibaba pipeline pct; others → SERP A1~A3 pct
      if (matrixMode === 'ali') return getAliPct(row) ?? -1;
      return row.completion_pct ?? -1;
    };
    return [...matrixRows].sort((a, b) => sortDir === 'desc' ? getScore(b) - getScore(a) : getScore(a) - getScore(b));
  }, [matrixRows, mSortBy, sortDir, matrixMode]);

  // Reset offset when filters change
  useEffect(() => { setMOffset(0); }, [mProjectId, mGroupId, mTag, mSearch, mSortBy, mLimit]);

  // Reset Ali stage selection when switching away from Ali mode
  useEffect(() => { if (matrixMode !== 'ali') setSelectedAliStages(new Set()); }, [matrixMode]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setMSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const batchAdvanceMutation = useMutation({
    mutationFn: () => alibabaApi.batchAdvancePipeline(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['pipeline-overview'] });
      toast.success(`批次推進: ${res.data?.advanced ?? 0} 步驟`);
    },
    onError: (err: Error) => toast.error(`批次推進失敗: ${err.message}`),
  });

  // Immediate process: collect all incomplete task_ids for selected keywords × stages, then priority-crawl
  const handleImmediateProcess = async () => {
    if (selectedKeywords.size === 0 || selectedStages.size === 0) return;
    setIsProcessingNow(true);
    try {
      const keywords = Array.from(selectedKeywords);
      const stages = Array.from(selectedStages) as ('a1'|'a2'|'a3')[];
      const taskIds: string[] = [];
      let noTaskCount = 0;
      for (const kw of keywords) {
        for (const stage of stages) {
          const res = await alibabaApi.getKeywordUrlDetail(kw, stage, mProjectId || undefined);
          // Split: items with no scraping task (null) vs items that have a task id
          const topItems = (res.data ?? []).filter(
            (d) => d.rank_group === 'top' && !d.stage_done && !d.is_blocklisted
          );
          const noTaskItems  = topItems.filter(d => !d.scraping_task_id);
          const hasTaskItems = topItems.filter(d => !!d.scraping_task_id);
          taskIds.push(...hasTaskItems.map(d => d.scraping_task_id!));
          noTaskCount += noTaskItems.length;
        }
      }
      const unique = [...new Set(taskIds)];
      if (unique.length === 0 && noTaskCount === 0) {
        toast.info('所選 TOP 20 項目已全部完成，無需處理');
        return;
      }

      // Auto-create missing tasks: collect unique serp_request_ids for no-task items
      if (noTaskCount > 0) {
        const requestIds = new Set<string>();
        for (const kw of keywords) {
          for (const stage of stages) {
            const res2 = await alibabaApi.getKeywordUrlDetail(kw, stage, mProjectId || undefined);
            (res2.data ?? [])
              .filter(d => d.rank_group === 'top' && !d.stage_done && !d.is_blocklisted && !d.scraping_task_id && d.serp_request_id)
              .forEach(d => requestIds.add(d.serp_request_id!));
          }
        }
        if (requestIds.size > 0) {
          toast.info(`正在為 ${noTaskCount} 筆無任務 URL 建立抓取任務...`);
          for (const rid of requestIds) {
            try { await serpApi.autoScrape(rid); } catch { /* continue */ }
          }
          // Re-fetch task IDs after creation
          taskIds.length = 0;
          for (const kw of keywords) {
            for (const stage of stages) {
              const res3 = await alibabaApi.getKeywordUrlDetail(kw, stage, mProjectId || undefined);
              const created = (res3.data ?? [])
                .filter(d => d.rank_group === 'top' && !d.stage_done && !d.is_blocklisted && !!d.scraping_task_id)
                .map(d => d.scraping_task_id!);
              taskIds.push(...created);
            }
          }
        } else {
          toast.warning(`${noTaskCount} 筆 TOP URL 缺少 SERP 分析來源，無法自動建任務。請重新分析此關鍵字。`);
        }
      }

      const uniqueFinal = [...new Set(taskIds)];
      if (uniqueFinal.length === 0) {
        toast.warning('所有 TOP URL 均為無任務狀態，請重新執行 SERP 分析後再試。');
        return;
      }
      await alibabaApi.priorityCrawl(uniqueFinal, true);
      toast.success(`已立刻排入 ${uniqueFinal.length} 筆 TOP 20，優先爬取中`);
      queryClient.invalidateQueries({ queryKey: ['pipeline-keyword-matrix'] });
      setSelectedKeywords(new Set());
    } catch (err: unknown) {
      toast.error(`處理失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsProcessingNow(false);
    }
  };

  // Batch SERP LLM analysis trigger for selected keywords
  const handleBatchSerpAnalysis = async () => {
    if (selectedKeywords.size === 0) return;
    setIsTriggeringSerpBatch(true);
    let triggered = 0;
    let skipped = 0;
    try {
      for (const kw of selectedKeywords) {
        const item = serpAnalysisMap.get(kw);
        if (!item) { skipped++; continue; }
        if (!item.analysis_ready) { skipped++; continue; }
        try {
          await serpAnalysisApi.trigger(item.request_id, item.keyword);
          triggered++;
        } catch { skipped++; }
      }
      toast.success(`已觸發 ${triggered} 個關鍵字 SERP 分析${skipped > 0 ? `（${skipped} 筆不符條件略過）` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['analysis-reports'] });
      setSelectedKeywords(new Set());
    } catch (err: unknown) {
      toast.error(`批次分析失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsTriggeringSerpBatch(false);
    }
  };

  // Ali immediate process: reset ALL Ali stages to pending for selected keywords, then trigger batch-advance
  const handleAliImmediateProcess = async () => {
    if (selectedKeywords.size === 0) return;
    setIsAliProcessingNow(true);
    try {
      const allAliStages = ['A-4', 'B-1', 'B-2', 'B-3', 'B-4', 'C-1', 'C-2', 'C-3', 'C-4', 'C-5'];
      const res = await alibabaApi.retrigerAliStages(
        Array.from(selectedKeywords),
        allAliStages,
      );
      const d = res.data;
      await alibabaApi.batchAdvancePipeline();
      toast.success(`已重置 ${d?.reset ?? 0} 個步驟（${d?.task_count ?? 0} 個任務）並立即推進 Pipeline`);
      queryClient.invalidateQueries({ queryKey: ['pipeline-keyword-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-overview'] });
      setSelectedKeywords(new Set());
    } catch (err: unknown) {
      toast.error(`Ali 立刻處理失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsAliProcessingNow(false);
    }
  };

  // Ali stage retrigger: reset pipeline_steps to pending for selected keywords × Ali stages
  const handleAliRetrigger = async () => {
    if (selectedKeywords.size === 0 || selectedAliStages.size === 0) return;
    setIsRetriggeringAli(true);
    try {
      const res = await alibabaApi.retrigerAliStages(
        Array.from(selectedKeywords),
        Array.from(selectedAliStages),
      );
      const d = res.data;
      toast.success(`已重置 ${d?.reset ?? 0} 個步驟（${d?.task_count ?? 0} 個任務）— 等待下次 batch-advance 推進`);
      queryClient.invalidateQueries({ queryKey: ['pipeline-keyword-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-overview'] });
      setSelectedKeywords(new Set());
      setSelectedAliStages(new Set());
    } catch (err: unknown) {
      toast.error(`補跑失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsRetriggeringAli(false);
    }
  };

  const [retryingAll, setRetryingAll] = useState(false);
  const handleRetryAll = async () => {
    if (failedItems.length === 0) return;
    setRetryingAll(true);
    try {
      await Promise.all(failedItems.map(({ taskId, step }) => alibabaApi.retryPipelineStep(taskId, step.step_id)));
      queryClient.invalidateQueries({ queryKey: ['pipeline-overview'] });
      toast.success(`已重新排程 ${failedItems.length} 個失敗步驟`);
    } catch (err: unknown) {
      toast.error(`部分步驟重試失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRetryingAll(false);
    }
  };

  const handleRetry = (taskId: string, stepId: string) => {
    alibabaApi.retryPipelineStep(taskId, stepId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['pipeline-overview'] });
        toast.success(`已重試 ${stepId}`);
      })
      .catch((err: Error) => toast.error(`重試失敗: ${err.message}`));
  };

  // Aggregate stats
  const totalSteps = overview.reduce((sum, t) => sum + t.total, 0);
  const totalCompleted = overview.reduce((sum, t) => sum + t.completed, 0);
  const totalFailed = overview.reduce((sum, t) => sum + t.failed, 0);
  const completionPct = totalSteps > 0 ? Math.round((totalCompleted / totalSteps) * 100) : 0;
  const failurePct = totalSteps > 0 ? Math.round((totalFailed / totalSteps) * 100) : 0;

  // Step distribution across all tasks
  const stepDistribution = new Map<string, { completed: number; failed: number; pending: number; running: number }>();
  for (const task of overview) {
    for (const step of task.steps) {
      const existing = stepDistribution.get(step.step_id) ?? { completed: 0, failed: 0, pending: 0, running: 0 };
      if (step.status === 'completed') existing.completed++;
      else if (step.status === 'failed') existing.failed++;
      else if (step.status === 'running') existing.running++;
      else existing.pending++;
      stepDistribution.set(step.step_id, existing);
    }
  }

  // Failed tasks list
  const failedItems = overview.flatMap(task =>
    task.steps
      .filter(s => s.status === 'failed')
      .map(s => ({ taskId: task.task_id, url: task.url, step: s }))
  );

  // Cell drill-down modal — rendered via {UrlDetailModal()} in JSX
  function UrlDetailModal() {
    if (!cellDetail) return null;
    const notDone = detailItems.filter(d => !d.stage_done && d.scraping_task_id);
    return (
      <Dialog open={!!cellDetail} onOpenChange={open => !open && setCellDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{cellDetail.keyword} — {cellDetail.label}</DialogTitle>
            <DialogDescription>
              {detailItems.filter(d => d.stage_done).length}/{detailItems.length} 已完成
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            {detailLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b bg-muted/50">
                    <th className="px-2 py-1.5 text-center w-10">#</th>
                    <th className="px-2 py-1.5 text-left">網域</th>
                    <th className="px-2 py-1.5 text-center w-12">類型</th>
                    <th className="px-2 py-1.5 text-center w-12">狀態</th>
                    {cellDetail.stage === 'a1' && <>
                      <th className="px-1 py-1.5 text-center text-[10px] w-10">HTML</th>
                      <th className="px-1 py-1.5 text-center text-[10px] w-10">H標</th>
                      <th className="px-1 py-1.5 text-center text-[10px] w-10">MD</th>
                      <th className="px-1 py-1.5 text-center text-[10px] w-10">Nav</th>
                    </>}
                    {cellDetail.stage === 'a3' && <th className="px-1 py-1.5 text-center text-[10px] w-14">圖片數</th>}
                    <th className="px-2 py-1.5 text-left text-[10px] text-muted-foreground">錯誤原因</th>
                  </tr>
                </thead>
                <tbody>
                  {detailItems.map(item => {
                    const isTop = item.rank_group === 'top';
                    const rowBg = item.is_blocklisted
                      ? 'bg-red-50/60'
                      : item.stage_done
                        ? 'bg-green-50/40'
                        : '';
                    return (
                      <tr key={item.url} className={`border-b hover:bg-accent/30 ${rowBg}`} title={item.url}>
                        {/* Rank badge */}
                        <td className="px-2 py-1 text-center">
                          <span className={`inline-block w-7 text-center font-bold text-[11px] rounded px-0.5 ${isTop ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                            {item.rank_absolute}
                          </span>
                        </td>
                        {/* Domain + blacklist badge */}
                        <td className="px-2 py-1 font-mono truncate max-w-[260px]">
                          {item.is_blocklisted && (
                            <span className="inline-block mr-1 text-[9px] bg-red-100 text-red-600 px-1 rounded font-semibold">🚫黑名單</span>
                          )}
                          {item.domain}
                        </td>
                        {/* TOP / 非TOP */}
                        <td className="px-2 py-1 text-center">
                          <span className={`text-[10px] px-1 rounded ${isTop ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                            {isTop ? 'TOP' : '非TOP'}
                          </span>
                        </td>
                        {/* Stage done status */}
                        <td className="px-2 py-1 text-center">
                          {item.stage_done
                            ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mx-auto" />
                            : item.task_status === 'pending' || !item.task_status
                              ? <Clock className="h-3.5 w-3.5 text-gray-400 mx-auto" />
                              : item.task_status === 'running'
                                ? <Loader2 className="h-3.5 w-3.5 text-blue-400 mx-auto animate-spin" />
                                : <AlertTriangle className="h-3.5 w-3.5 text-red-400 mx-auto" />}
                        </td>
                        {/* A-1 sub-columns */}
                        {cellDetail.stage === 'a1' && <>
                          <td className="px-1 py-1 text-center">{item.html_ssr_done ? '✅' : '❌'}</td>
                          <td className="px-1 py-1 text-center">{item.h_structure_done ? '✅' : '❌'}</td>
                          <td className="px-1 py-1 text-center">{item.markdown_done ? '✅' : '❌'}</td>
                          <td className="px-1 py-1 text-center">{item.nav_tree_done ? '✅' : '❌'}</td>
                        </>}
                        {/* A-3 image count */}
                        {cellDetail.stage === 'a3' && <td className="px-1 py-1 text-center">{item.images_count}</td>}
                        {/* Error reason */}
                        <td className="px-2 py-1 text-[10px] text-red-500 truncate max-w-[200px]" title={item.task_error ?? ''}>
                          {item.task_error ? item.task_error.slice(0, 80) : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground flex-1">未完成: {notDone.length} 筆</span>
            <Button
              size="sm"
              disabled={notDone.length === 0}
              onClick={async () => {
                const ids = notDone.map(d => d.scraping_task_id!);
                await alibabaApi.priorityCrawl(ids, true);
                toast.success(`已排程 ${ids.length} 筆優先爬取`);
                setCellDetail(null);
              }}
            >
              排程未完成的 {notDone.length} 筆
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="space-y-6">
      <BrokenChainPanel />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pipeline 監控</h1>
          <p className="text-muted-foreground text-sm">全局 Pipeline 執行狀態</p>
        </div>
        <Button
          onClick={() => batchAdvanceMutation.mutate()}
          disabled={batchAdvanceMutation.isPending}
        >
          {batchAdvanceMutation.isPending ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" />推進中...</>
          ) : (
            <><Play className="h-4 w-4 mr-1" />批次推進</>
          )}
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> 總步驟數</div>
            <div className="text-3xl font-bold mt-1">{totalSteps}</div>
            <div className="text-xs text-muted-foreground">{overview.length} 個任務</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-500" /> 完成率</div>
            <div className="text-3xl font-bold mt-1 text-green-600">{completionPct}%</div>
            <Progress value={completionPct} className="h-2 mt-1" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-red-500" /> 失敗率</div>
            <div className="text-3xl font-bold mt-1 text-red-600">{failurePct}%</div>
            <div className="text-xs text-muted-foreground">{totalFailed} 步驟失敗</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="text-xs text-muted-foreground">進行中</div>
            <div className="text-3xl font-bold mt-1 text-blue-600">
              {overview.reduce((sum, t) => sum + t.steps.filter(s => s.status === 'running').length, 0)}
            </div>
            <div className="text-xs text-muted-foreground">
              待處理: {totalSteps - totalCompleted - totalFailed - overview.reduce((sum, t) => sum + t.steps.filter(s => s.status === 'running').length, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Step distribution */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">步驟分佈</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-11">
            {/* SERP: A-1~A-3; Ali PD: display names PD-1~PD-8 (DB: A-4~C-3) */}
            {['A-1','A-2','A-3','PD-1','PD-2','PD-3','PD-4','PD-5','PD-6','PD-7','PD-8'].map((displayId, i) => {
              // Map display name → actual DB step_id for lookup
              const stepId = i < 3 ? displayId : ['A-4','B-1','B-2','B-3','B-4','C-1','C-2','C-3'][i-3];
              const dist = stepDistribution.get(stepId);
              const total = dist ? dist.completed + dist.failed + dist.pending + dist.running : 0;
              return (
                <div key={displayId} className="text-center p-2 rounded border">
                  <div className="text-xs font-mono font-semibold">{displayId}</div>
                  {dist && total > 0 ? (
                    <div className="mt-1 space-y-0.5">
                      {dist.completed > 0 && <div className="text-[10px] text-green-600">{dist.completed}✓</div>}
                      {dist.failed > 0 && <div className="text-[10px] text-red-600">{dist.failed}✗</div>}
                      {dist.running > 0 && <div className="text-[10px] text-blue-600">{dist.running}⟳</div>}
                      {dist.pending > 0 && <div className="text-[10px] text-gray-400">{dist.pending}…</div>}
                    </div>
                  ) : (
                    <div className="text-[10px] text-muted-foreground mt-1">—</div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ─── Ali 模式：三層覆蓋率盤點 + 後台動作 ─── */}
      {matrixMode === 'ali' && aliOverview && (
        <div className="space-y-3 mb-2">
          {/* Three-layer coverage cards */}
          <div className="grid grid-cols-3 gap-3">
            {/* SC Layer */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="text-xs font-semibold text-blue-700 mb-2">SC 分類層</div>
              <div className="text-xs space-y-1">
                <div>關鍵字：{aliOverview.sc?.keywords_total ?? '—'}</div>
                <div>供應商：{aliOverview.sc?.suppliers_found ?? '—'}</div>
                <div>分類：{aliOverview.sc?.nav_categories ?? '—'}</div>
              </div>
            </div>

            {/* CO Layer */}
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <div className="text-xs font-semibold text-green-700 mb-2">CO 公司層</div>
              <div className="text-xs space-y-1">
                <div>QS 完成：{aliOverview.co?.qs_done ?? '—'} / {aliOverview.co?.total ?? '—'}</div>
                <div>BA 廠區：{aliOverview.co?.ba_done ?? '—'}</div>
                <div>產品列表：{aliOverview.co?.has_products ?? '—'} 家</div>
                {(aliOverview.co?.qs_pending ?? 0) > 0 && (
                  <div className="text-orange-600">待爬：{aliOverview.co.qs_pending} 家</div>
                )}
              </div>
            </div>

            {/* PD Layer */}
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <div className="text-xs font-semibold text-purple-700 mb-2">PD 商品層</div>
              <div className="text-xs space-y-1">
                <div>BA 解析：{aliOverview.pd?.ba_parsed ?? '—'} / {aliOverview.pd?.total ?? '—'}</div>
                <div>有截圖：{aliOverview.pd?.has_screenshot ?? '—'}</div>
                <div>有 MD：{aliOverview.pd?.has_markdown ?? '—'}</div>
                <div>QS 省費：{aliOverview.qs_savings?.ba_credits_saved ?? 0} 次</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="border rounded-lg p-3">
            <div className="text-xs font-semibold text-muted-foreground mb-2">後台動作</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                className="text-xs py-1.5 px-3 rounded border hover:bg-blue-50 border-blue-300"
                onClick={() => triggerAliAction('trigger_co_crawl')}
              >
                ↺ CO QS 補爬（自動排程）
              </button>
              <button
                className="text-xs py-1.5 px-3 rounded border hover:bg-orange-50 border-orange-300"
                onClick={() => triggerAliAction('trigger_ba_factory')}
              >
                ↺ BA 廠區（限 2 家）
              </button>
              <button
                className="text-xs py-1.5 px-3 rounded border hover:bg-green-50 border-green-300"
                onClick={() => triggerAliAction('trigger_co_products')}
              >
                ↺ CO 產品列表
              </button>
              <button
                className="text-xs py-1.5 px-3 rounded border hover:bg-purple-50 border-purple-300"
                onClick={() => triggerAliAction('trigger_c4_backfill')}
              >
                ↺ C-4 截圖補觸發（10 筆）
              </button>
            </div>
          </div>
        </div>
      )}


      {/* ─── 關鍵字完成度矩陣 / Alibaba SC · CO · PD（Tab 列永遠顯示）─── */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base">{matrixMode === 'ali' ? '③ 産品頁 Pipeline（PD-1~PD-10）' : '關鍵字完成度矩陣'}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {matrixMode === 'ali'
                  ? <>格式: 完成/總計（阿里巴巴產品合計）　　<span className="text-green-600">≥80%</span> <span className="text-yellow-600">50-79%</span> <span className="text-red-500">&lt;50%</span></>
                  : <>格式: TOP(rank 1-20) ｜ 非TOP(rank 21+)　　<span className="text-green-600">≥80%</span> <span className="text-yellow-600">50-79%</span> <span className="text-red-500">&lt;50%</span></>
                }
              </p>
            </div>
            {matrixTotal > 0 && (
              <span className="text-xs text-muted-foreground">
                顯示 {mOffset + 1}–{Math.min(mOffset + mLimit, matrixTotal)} / 共 {matrixTotal} 筆
              </span>
            )}
          </div>
          {/* Filter bar */}
          <div className="flex flex-wrap gap-2 pt-2">
            {/* Mode toggle */}
            <div className="flex rounded border overflow-hidden text-xs h-7">
              {(['serp', 'ali', 'serp-llm', 'all'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setMatrixMode(m)}
                  className={`px-2.5 h-full transition-colors ${matrixMode === m ? 'bg-primary text-primary-foreground font-medium' : 'bg-background hover:bg-muted'}`}
                >
                  {m === 'serp' ? 'SERP (A1~A3)' : m === 'ali' ? 'Alibaba' : m === 'serp-llm' ? 'SERP（A4）' : '全部'}
                </button>
              ))}
            </div>
            {/* Ali sub-tabs — only shown in Alibaba mode */}
            {matrixMode === 'ali' && (
              <div className="flex rounded border overflow-hidden text-xs h-7 border-orange-300">
                {([['sc','① SC 分類頁'],['co','② CO 公司頁'],['pd','③ PD 産品頁']] as const).map(([tab, label]) => (
                  <button
                    key={tab}
                    onClick={() => setAliSubTab(tab)}
                    className={`px-2.5 h-full transition-colors ${aliSubTab === tab ? 'bg-orange-500 text-white font-medium' : 'bg-background hover:bg-orange-50 text-orange-700'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {/* Project */}
            <Select value={mProjectId || '__all__'} onValueChange={v => { setMProjectId(v === '__all__' ? '' : v); setMGroupId(''); setMTag(''); }}>
              <SelectTrigger className="h-7 text-xs w-36"><SelectValue placeholder="全部專案" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">全部專案</SelectItem>
                {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Group */}
            {mProjectId && (
              <Select value={mGroupId || '__all__'} onValueChange={v => setMGroupId(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-7 text-xs w-36"><SelectValue placeholder="全部群組" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">全部群組</SelectItem>
                  {groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {/* Tag */}
            {mProjectId && allTags.length > 0 && (
              <Select value={mTag || '__all__'} onValueChange={v => setMTag(v === '__all__' ? '' : v)}>
                <SelectTrigger className="h-7 text-xs w-32"><SelectValue placeholder="全部 Tag" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">全部 Tag</SelectItem>
                  {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                className="h-7 text-xs pl-7 w-44"
                placeholder="搜尋關鍵字..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>
            {/* Sort shortcut */}
            <div className="flex rounded border overflow-hidden text-xs h-7">
              <button onClick={() => { setMSortBy('completion'); setSortDir('desc'); }} className={`px-2.5 h-full transition-colors ${mSortBy === 'completion' ? 'bg-primary text-primary-foreground font-medium' : 'bg-background hover:bg-muted'}`}>完成率↓</button>
              <button onClick={() => { setMSortBy('keyword'); setSortDir('asc'); }}    className={`px-2.5 h-full transition-colors ${mSortBy === 'keyword'    ? 'bg-primary text-primary-foreground font-medium' : 'bg-background hover:bg-muted'}`}>字母↑</button>
            </div>
            {/* Limit */}
            <Select value={String(mLimit)} onValueChange={v => setMLimit(Number(v) as typeof mLimit)}>
              <SelectTrigger className="h-7 text-xs w-20"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LIMIT_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n} 筆</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Immediate process — SERP mode: A1~A3 priority crawl / Ali mode: reset all Ali stages + batch-advance */}
            {matrixMode === 'ali' ? (
              <Button size="sm" className="h-7 text-xs gap-1 bg-orange-500 hover:bg-orange-600 text-white"
                title="重置所選關鍵字的全部 A-4~C-5 步驟並立即推進 Pipeline"
                disabled={selectedKeywords.size === 0 || isAliProcessingNow}
                onClick={handleAliImmediateProcess}>
                {isAliProcessingNow
                  ? <><Loader2 className="h-3 w-3 animate-spin" />處理中...</>
                  : <><Zap className="h-3 w-3" />Ali 立刻處理 ({selectedKeywords.size} 筆)</>}
              </Button>
            ) : (
              <Button size="sm" className="h-7 text-xs gap-1 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={selectedKeywords.size === 0 || selectedStages.size === 0 || isProcessingNow}
                onClick={handleImmediateProcess}>
                {isProcessingNow
                  ? <><Loader2 className="h-3 w-3 animate-spin" />收集中...</>
                  : <><Zap className="h-3 w-3" />立刻處理 ({selectedKeywords.size} 筆)</>}
              </Button>
            )}
            {/* Batch SERP analysis trigger (A-4 SERP) */}
            {(matrixMode === 'serp' || matrixMode === 'all') && (
              <Button size="sm" className="h-7 text-xs gap-1 bg-purple-500 hover:bg-purple-600 text-white"
                disabled={selectedKeywords.size === 0 || isTriggeringSerpBatch}
                onClick={handleBatchSerpAnalysis}>
                {isTriggeringSerpBatch
                  ? <><Loader2 className="h-3 w-3 animate-spin" />觸發中...</>
                  : <><BarChart3 className="h-3 w-3" />批次分析 ({selectedKeywords.size} 筆)</>}
              </Button>
            )}
            {/* Ali stage retrigger button (Alibaba mode only) */}
            {matrixMode === 'ali' && (
              <Button size="sm" className="h-7 text-xs gap-1 bg-blue-500 hover:bg-blue-600 text-white"
                disabled={selectedKeywords.size === 0 || selectedAliStages.size === 0 || isRetriggeringAli}
                onClick={handleAliRetrigger}>
                {isRetriggeringAli
                  ? <><Loader2 className="h-3 w-3 animate-spin" />重置中...</>
                  : <><RotateCcw className="h-3 w-3" />補跑 Ali ({selectedKeywords.size}筆 × {selectedAliStages.size}步)</>}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* ── Ali SC 分類頁 ── */}
          {matrixMode === 'ali' && aliSubTab === 'sc' && (
            aliSearchProgress.length === 0
              ? <div className="py-8 text-center text-xs text-muted-foreground">無 SC 資料（尚未爬取分類頁）</div>
              : <>
                  {/* SC batch bar */}
                  <div className="flex gap-2 px-3 py-2 border-b bg-muted/10 flex-wrap items-center">
                    <span className="text-xs text-muted-foreground">
                      {selectedScKeywords.size > 0 ? `已選 ${selectedScKeywords.size} 個關鍵字` : '全部關鍵字'}
                    </span>
                    <div className="flex-1" />
                    <button
                      disabled={isBatchScRetrying || aliSearchProgress.length === 0}
                      onClick={async () => {
                        setIsBatchScRetrying(true);
                        try {
                          const res = await fetch('/api/alibaba/searches/retry-failed', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ filter: 'failed', keywords: selectedScKeywords.size > 0 ? Array.from(selectedScKeywords) : undefined })
                          });
                          if (res.ok) { toast.success('已觸發補跑失敗頁'); queryClient.invalidateQueries({ queryKey: ['ali-search-progress'] }); }
                          else toast.error('補跑失敗');
                        } catch { toast.error('補跑失敗'); }
                        finally { setIsBatchScRetrying(false); }
                      }}
                      className="px-3 py-1.5 rounded text-xs bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
                    >
                      {isBatchScRetrying ? '補跑中…' : '補跑失敗頁🔶'}
                    </button>
                    <button
                      disabled={isBatchScRetrying || aliSearchProgress.length === 0}
                      onClick={async () => {
                        setIsBatchScRetrying(true);
                        try {
                          const res = await fetch('/api/alibaba/searches/retry-failed', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ filter: 'pending', keywords: selectedScKeywords.size > 0 ? Array.from(selectedScKeywords) : undefined })
                          });
                          if (res.ok) { toast.success('已觸發補跑待爬頁'); queryClient.invalidateQueries({ queryKey: ['ali-search-progress'] }); }
                          else toast.error('補跑失敗');
                        } catch { toast.error('補跑失敗'); }
                        finally { setIsBatchScRetrying(false); }
                      }}
                      className="px-3 py-1.5 rounded text-xs bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                      補跑待爬頁🔵
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="px-2 py-1.5 w-8 sticky left-0 bg-muted/50">
                          <input type="checkbox" className="h-3 w-3 cursor-pointer"
                            checked={aliSearchProgress.length > 0 && selectedScKeywords.size === aliSearchProgress.length}
                            onChange={e => setSelectedScKeywords(e.target.checked ? new Set(aliSearchProgress.map(r => r.keyword)) : new Set())}
                          />
                        </th>
                        <th className="text-left px-3 py-1.5 font-medium sticky left-8 bg-muted/50 min-w-[180px]">關鍵字</th>
                        <th className="px-2 py-1.5 text-center font-medium text-blue-600">搜尋頁<br/><span className="text-[9px] font-normal text-muted-foreground">done/total</span></th>
                        <th className="px-2 py-1.5 text-center font-medium text-red-500">失敗頁</th>
                        <th className="px-2 py-1.5 text-center font-medium text-gray-400">待爬頁</th>
                        <th className="px-2 py-1.5 text-center font-medium text-green-600">產品數</th>
                        <th className="px-2 py-1.5 text-center font-medium text-purple-600">公司 URL<br/><span className="text-[9px] font-normal text-muted-foreground">→ CO層</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {aliSearchProgress.map(row => {
                        const pagePct = row.total_pages > 0 ? row.pages_done / row.total_pages : -1;
                        const pageClr = pagePct < 0 ? 'text-gray-300' : pagePct >= 0.8 ? 'text-green-600' : pagePct >= 0.5 ? 'text-yellow-600' : 'text-red-500';
                        const isSelected = scDrilldown?.keyword === row.keyword;
                        return (
                          <React.Fragment key={row.keyword}>
                            <tr className={`border-b cursor-pointer ${isSelected ? 'bg-blue-50' : 'hover:bg-accent/30'}`}
                              onClick={() => { setScDrilldown(isSelected ? null : { keyword: row.keyword }); setScFilter('all'); }}>
                              <td className="px-2 py-1.5 w-8 sticky left-0 bg-background"
                                onClick={e => e.stopPropagation()}>
                                <input type="checkbox" className="h-3 w-3 cursor-pointer"
                                  checked={selectedScKeywords.has(row.keyword)}
                                  onChange={e => {
                                    const s = new Set(selectedScKeywords);
                                    e.target.checked ? s.add(row.keyword) : s.delete(row.keyword);
                                    setSelectedScKeywords(s);
                                  }}
                                />
                              </td>
                              <td className="px-3 py-1.5 font-mono sticky left-8 bg-background truncate max-w-[200px]">
                                <span className={isSelected ? 'text-blue-700 font-semibold' : ''}>{row.keyword}</span>
                              </td>
                              <td className="px-2 py-1.5 text-center"><span className={`font-mono font-semibold ${pageClr}`}>{row.pages_done}/{row.total_pages}</span></td>
                              <td className="px-2 py-1.5 text-center">{row.pages_failed > 0 ? <span className="text-red-500 font-mono">{row.pages_failed}</span> : <span className="text-gray-300">—</span>}</td>
                              <td className="px-2 py-1.5 text-center">{row.pages_pending > 0 ? <span className="text-gray-400 font-mono">{row.pages_pending}</span> : <span className="text-gray-300">—</span>}</td>
                              <td className="px-2 py-1.5 text-center"><span className="text-green-600 font-mono font-semibold">{row.products_found}</span></td>
                              <td className="px-2 py-1.5 text-center"><span className="text-purple-600 font-mono">{row.company_urls_found}</span></td>
                            </tr>
                            {isSelected && (
                              <tr>
                                <td colSpan={7} className="p-0 bg-blue-50/30 border-b">
                                  <div className="p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-semibold text-blue-700">
                                        {scDrilldown!.keyword} — 分頁詳情
                                        {scDrilldownData && <span className="ml-1 text-muted-foreground font-normal">（{scDrilldownData.data.length} 頁）</span>}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        {selectedScPages.size > 0 && (
                                          <button
                                            onClick={async () => {
                                              const ids = Array.from(selectedScPages);
                                              try {
                                                const res = await fetch('/api/alibaba/searches/retry-failed', {
                                                  method: 'POST',
                                                  headers: { 'Content-Type': 'application/json' },
                                                  body: JSON.stringify({ search_ids: ids })
                                                });
                                                if (res.ok) { toast.success(`已補跑 ${ids.length} 頁`); setSelectedScPages(new Set()); queryClient.invalidateQueries({ queryKey: ['ali-sc-pages'] }); }
                                                else toast.error('補跑失敗');
                                              } catch { toast.error('補跑失敗'); }
                                            }}
                                            className="px-2 py-1 rounded text-xs bg-blue-500 text-white hover:bg-blue-600"
                                          >
                                            補跑選定（{selectedScPages.size}頁）🔵
                                          </button>
                                        )}
                                        <button onClick={e => { e.stopPropagation(); setScDrilldown(null); setSelectedScPages(new Set()); }} className="text-xs text-muted-foreground hover:text-foreground">✕ 收起</button>
                                      </div>
                                    </div>
                                    {scDrilldownFetching && <div className="text-center py-4 text-xs text-muted-foreground">載入中…</div>}
                                    {/* SC inline filter bar */}
                                    <div className="flex items-center gap-1 mb-2 flex-wrap">
                                      {(['all','completed','failed','pending'] as const).map(f => (
                                        <button
                                          key={f}
                                          onClick={() => setScFilter(f)}
                                          className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                                            scFilter === f ? 'bg-blue-500 text-white border-blue-500' : 'bg-background hover:bg-muted border-muted-foreground/30'
                                          }`}
                                        >
                                          {f === 'all' ? '全部' : f === 'completed' ? '✅ 已完成' : f === 'failed' ? '❌ 失敗' : '⏳ 待爬'}
                                        </button>
                                      ))}
                                      <div className="flex-1" />
                                      <button
                                        onClick={async () => {
                                          const failedIds = (scDrilldownData?.data ?? [])
                                            .filter(r => r.status === 'failed')
                                            .map(r => r.search_id);
                                          if (failedIds.length === 0) { toast.info('無失敗頁'); return; }
                                          try {
                                            const res = await fetch('/api/alibaba/searches/retry-failed', {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({ search_ids: failedIds })
                                            });
                                            if (res.ok) { toast.success(`已補跑 ${failedIds.length} 頁`); queryClient.invalidateQueries({ queryKey: ['ali-sc-pages'] }); }
                                            else toast.error('補跑失敗');
                                          } catch { toast.error('補跑失敗'); }
                                        }}
                                        className="px-2 py-0.5 rounded text-xs bg-orange-500 text-white hover:bg-orange-600"
                                      >
                                        補跑失敗頁🔶
                                      </button>
                                      <button
                                        onClick={async () => {
                                          const allIds = (scDrilldownData?.data ?? []).map(r => r.search_id);
                                          if (allIds.length === 0) return;
                                          try {
                                            const res = await fetch('/api/alibaba/searches/retry-failed', {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({ search_ids: allIds })
                                            });
                                            if (res.ok) { toast.success(`已補跑全部 ${allIds.length} 頁`); queryClient.invalidateQueries({ queryKey: ['ali-sc-pages'] }); }
                                            else toast.error('補跑失敗');
                                          } catch { toast.error('補跑失敗'); }
                                        }}
                                        className="px-2 py-0.5 rounded text-xs bg-blue-500 text-white hover:bg-blue-600"
                                      >
                                        全部重跑🔵
                                      </button>
                                    </div>
                                    {scDrilldownData && (
                                      <table className="w-full text-xs border-collapse">
                                        <thead><tr className="bg-muted/50 border-b">
                                          <th className="px-2 py-1.5 w-6">
                                            <input type="checkbox" className="h-3 w-3 cursor-pointer"
                                              checked={scDrilldownData.data.length > 0 && selectedScPages.size === scDrilldownData.data.length}
                                              onChange={e => setSelectedScPages(e.target.checked ? new Set(scDrilldownData.data.map(r => r.search_id)) : new Set())}
                                            />
                                          </th>
                                          <th className="text-left px-2 py-1.5 font-medium">搜尋頁 URL</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-green-600">產品數</th>
                                          <th className="px-2 py-1.5 text-center font-medium text-purple-600">公司數</th>
                                          <th className="px-2 py-1.5 text-center font-medium">狀態</th>
                                        </tr></thead>
                                        <tbody>
                                          {scDrilldownData.data.filter(r => {
                                            if (scFilter === 'all') return true;
                                            if (scFilter === 'completed') return r.status === 'completed';
                                            if (scFilter === 'failed') return r.status === 'failed';
                                            if (scFilter === 'pending') return r.status === 'running' || r.status === 'pending';
                                            return true;
                                          }).map(r => (
                                            <tr key={r.search_id} className="border-b hover:bg-accent/20">
                                              <td className="px-2 py-1.5">
                                                <input type="checkbox" className="h-3 w-3 cursor-pointer"
                                                  checked={selectedScPages.has(r.search_id)}
                                                  onChange={e => {
                                                    const s = new Set(selectedScPages);
                                                    e.target.checked ? s.add(r.search_id) : s.delete(r.search_id);
                                                    setSelectedScPages(s);
                                                  }}
                                                />
                                              </td>
                                              <td className="px-2 py-1.5">
                                                <span className="font-mono font-semibold">P{r.page_number}</span>
                                                {r.search_url && <div className="text-[10px] text-muted-foreground truncate max-w-[220px]">{r.search_url}</div>}
                                              </td>
                                              <td className="px-2 py-1.5 text-center font-mono text-green-600">{r.product_count}</td>
                                              <td className="px-2 py-1.5 text-center font-mono text-purple-600">{r.company_url_count}</td>
                                              <td className="px-2 py-1.5 text-center">{r.status === 'completed' ? '✅' : r.status === 'failed' ? '❌' : r.status === 'running' ? '⏳' : '○'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
          )}

          {/* ── Ali CO 公司頁 ── */}
          {matrixMode === 'ali' && aliSubTab === 'co' && (
            aliCompanyProgress.length === 0
              ? <div className="py-8 text-center text-xs text-muted-foreground">無 CO 資料</div>
              : <>
                  {/* CO batch bar */}
                  <div className="flex gap-2 px-3 py-2 border-b bg-muted/10 flex-wrap items-center">
                    <span className="text-xs text-muted-foreground">
                      {selectedCoKeywords.size > 0 ? `已選 ${selectedCoKeywords.size} 個關鍵字` : '全部關鍵字'}
                    </span>
                    <div className="flex-1" />
                    <button
                      disabled={isBatchCoRetrying}
                      onClick={async () => {
                        setIsBatchCoRetrying(true);
                        try {
                          await api.post('/alibaba/company-profiles/batch-crawl', {
                            filter: 'pending',
                            keyword: selectedCoKeywords.size === 1 ? Array.from(selectedCoKeywords)[0] : undefined,
                            limit: 20
                          });
                          toast.success('已觸發補爬未爬 Profile');
                          queryClient.invalidateQueries({ queryKey: ['ali-company-progress'] });
                        } catch { toast.error('補爬失敗'); }
                        finally { setIsBatchCoRetrying(false); }
                      }}
                      className="px-3 py-1.5 rounded text-xs bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
                    >
                      {isBatchCoRetrying ? '補爬中…' : '補爬未爬 Profile🔶'}
                    </button>
                    <button
                      disabled={isBatchCoRetrying}
                      onClick={async () => {
                        setIsBatchCoRetrying(true);
                        try {
                          await api.post('/alibaba/company-profiles/batch-crawl', {
                            filter: 'failed',
                            keyword: selectedCoKeywords.size === 1 ? Array.from(selectedCoKeywords)[0] : undefined,
                            limit: 20
                          });
                          toast.success('已觸發補爬失敗 Profile');
                          queryClient.invalidateQueries({ queryKey: ['ali-company-progress'] });
                        } catch { toast.error('補爬失敗'); }
                        finally { setIsBatchCoRetrying(false); }
                      }}
                      className="px-3 py-1.5 rounded text-xs bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                      補爬失敗🔵
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="px-2 py-1.5 w-8 sticky left-0 bg-muted/50">
                          <input type="checkbox" className="h-3 w-3 cursor-pointer"
                            checked={aliCompanyProgress.length > 0 && selectedCoKeywords.size === aliCompanyProgress.length}
                            onChange={e => setSelectedCoKeywords(e.target.checked ? new Set(aliCompanyProgress.map(r => r.keyword)) : new Set())}
                          />
                        </th>
                        <th className="text-left px-3 py-1.5 font-medium sticky left-8 bg-muted/50 min-w-[180px]">關鍵字</th>
                        <th className="px-2 py-1.5 text-center font-medium text-purple-600">公司 URL 數</th>
                        <th className="px-2 py-1.5 text-center font-medium text-green-600">已爬 Profile<br/><span className="text-[9px] font-normal text-muted-foreground">ali_company_profiles</span></th>
                        <th className="px-2 py-1.5 text-center font-medium text-blue-600">已解析 Nav</th>
                        <th className="px-2 py-1.5 text-center font-medium text-red-500">失敗</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aliCompanyProgress.map(row => {
                        const crawlPct = row.company_urls_found > 0 ? row.profiles_crawled / row.company_urls_found : -1;
                        const crawlClr = crawlPct < 0 ? 'text-gray-300' : crawlPct >= 0.8 ? 'text-green-600' : crawlPct >= 0.5 ? 'text-yellow-600' : crawlPct > 0 ? 'text-red-500' : 'text-gray-400';
                        const isSelected = coDrilldown?.keyword === row.keyword;
                        return (
                          <React.Fragment key={row.keyword}>
                            <tr className={`border-b cursor-pointer ${isSelected ? 'bg-green-50' : 'hover:bg-accent/30'}`}
                              onClick={() => setCoDrilldown(isSelected ? null : { keyword: row.keyword })}>
                              <td className="px-2 py-1.5 w-8 sticky left-0 bg-background"
                                onClick={e => e.stopPropagation()}>
                                <input type="checkbox" className="h-3 w-3 cursor-pointer"
                                  checked={selectedCoKeywords.has(row.keyword)}
                                  onChange={e => {
                                    const s = new Set(selectedCoKeywords);
                                    e.target.checked ? s.add(row.keyword) : s.delete(row.keyword);
                                    setSelectedCoKeywords(s);
                                  }}
                                />
                              </td>
                              <td className="px-3 py-1.5 font-mono sticky left-8 bg-background truncate max-w-[200px]">
                                <span className={isSelected ? 'text-green-700 font-semibold' : ''}>{row.keyword}</span>
                              </td>
                              <td className="px-2 py-1.5 text-center"><span className="text-purple-600 font-mono font-semibold">{row.company_urls_found}</span></td>
                              <td className="px-2 py-1.5 text-center"><span className={`font-mono font-semibold ${crawlClr}`}>{row.profiles_crawled}/{row.company_urls_found}</span></td>
                              <td className="px-2 py-1.5 text-center">{row.nav_crawled > 0 ? <span className="text-blue-600 font-mono">{row.nav_crawled}</span> : <span className="text-gray-300">—</span>}</td>
                              <td className="px-2 py-1.5 text-center">{row.profiles_failed > 0 ? <span className="text-red-500 font-mono">{row.profiles_failed}</span> : <span className="text-gray-300">—</span>}</td>
                            </tr>
                            {isSelected && (
                              <tr>
                                <td colSpan={6} className="p-0 bg-green-50/30 border-b">
                                  <div className="p-3">
                                    {/* CO inline header */}
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-semibold text-green-700">
                                        {coDrilldown!.keyword} — CO 公司頁詳情
                                        {coDrilldownData && <span className="ml-1 text-muted-foreground font-normal">（{coDrilldownData.data.length} 家）</span>}
                                      </span>
                                      <button onClick={e => { e.stopPropagation(); setCoDrilldown(null); setSelectedCoCompanies(new Set()); setCoFilter('all'); }} className="text-xs text-muted-foreground hover:text-foreground">✕ 收起</button>
                                    </div>
                                    {/* CO inline filter bar — 對齊線框稿 §3-B */}
                                    <div className="flex items-center gap-1 mb-2 flex-wrap">
                                      {(['all','crawled','pending','failed'] as const).map(f => (
                                        <button key={f} onClick={() => setCoFilter(f)}
                                          className={`px-2 py-0.5 rounded text-xs border transition-colors ${coFilter === f ? 'bg-green-600 text-white border-green-600' : 'bg-background hover:bg-muted border-muted-foreground/30'}`}>
                                          {f === 'all' ? '全部' : f === 'crawled' ? '✅ 已爬' : f === 'pending' ? '⏳ 未爬' : '❌ 失敗'}
                                        </button>
                                      ))}
                                      <div className="flex-1" />
                                      <button
                                        onClick={async () => {
                                          const urls = (coDrilldownData?.data ?? []).filter((c: CoCompanyRow) => !c.profile_status || c.profile_status === 'pending').map((c: CoCompanyRow) => c.company_url);
                                          if (!urls.length) { toast.info('無未爬公司'); return; }
                                          try {
                                            await api.post('/alibaba/company-profiles/batch-crawl', { company_urls: urls });
                                            toast.success(`已補爬 ${urls.length} 家`);
                                            queryClient.invalidateQueries({ queryKey: ['ali-co-companies'] });
                                          } catch { toast.error('補爬失敗'); }
                                        }}
                                        className="px-2 py-0.5 rounded text-xs bg-orange-500 text-white hover:bg-orange-600">補爬未爬🔶</button>
                                      <button
                                        onClick={async () => {
                                          const urls = (coDrilldownData?.data ?? []).filter((c: CoCompanyRow) => c.profile_status === 'failed').map((c: CoCompanyRow) => c.company_url);
                                          if (!urls.length) { toast.info('無失敗公司'); return; }
                                          try {
                                            await api.post('/alibaba/company-profiles/batch-crawl', { company_urls: urls });
                                            toast.success(`已補爬 ${urls.length} 家`);
                                            queryClient.invalidateQueries({ queryKey: ['ali-co-companies'] });
                                          } catch { toast.error('補爬失敗'); }
                                        }}
                                        className="px-2 py-0.5 rounded text-xs bg-blue-500 text-white hover:bg-blue-600">補爬失敗🔵</button>
                                      {selectedCoCompanies.size > 0 && (
                                        <button
                                          onClick={async () => {
                                            const urls = Array.from(selectedCoCompanies);
                                            try {
                                              await api.post('/alibaba/company-profiles/batch-crawl', { company_urls: urls });
                                              toast.success(`已補爬 ${urls.length} 家`);
                                              setSelectedCoCompanies(new Set());
                                              queryClient.invalidateQueries({ queryKey: ['ali-co-companies'] });
                                            } catch { toast.error('補爬失敗'); }
                                          }}
                                          className="px-2 py-0.5 rounded text-xs bg-blue-700 text-white hover:bg-blue-800">
                                          補爬選定（{selectedCoCompanies.size}家）🔵</button>
                                      )}
                                    </div>
                                    {coDrilldownFetching && <div className="text-center py-4 text-xs text-muted-foreground">載入中…</div>}
                                    {coDrilldownData && (
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs border-collapse">
                                          <thead><tr className="bg-muted/50 border-b">
                                            <th className="px-2 py-1.5 w-6">
                                              <input type="checkbox" className="h-3 w-3 cursor-pointer"
                                                checked={coDrilldownData.data.length > 0 && selectedCoCompanies.size === coDrilldownData.data.length}
                                                onChange={e => setSelectedCoCompanies(e.target.checked ? new Set(coDrilldownData.data.map((c: CoCompanyRow) => c.company_url)) : new Set())}
                                              />
                                            </th>
                                            <th className="text-left px-2 py-1.5 font-medium min-w-[200px]">公司名稱 / URL</th>
                                            <th className="px-2 py-1.5 text-center font-medium">QS 狀態</th>
                                            <th className="px-2 py-1.5 text-center font-medium text-green-600">BA 廠區</th>
                                            <th className="px-2 py-1.5 text-center font-medium text-yellow-600">評分</th>
                                            <th className="px-2 py-1.5 text-center font-medium text-blue-600">認證</th>
                                            <th className="px-2 py-1.5 text-center font-medium text-indigo-600">Nav 分類</th>
                                          </tr></thead>
                                          <tbody>
                                            {coDrilldownData.data.filter((c: CoCompanyRow) => {
                                              if (coFilter === 'all') return true;
                                              if (coFilter === 'crawled') return c.profile_status === 'completed';
                                              if (coFilter === 'failed') return c.profile_status === 'failed';
                                              if (coFilter === 'pending') return !c.profile_status || c.profile_status === 'pending';
                                              return true;
                                            }).map((c: CoCompanyRow, i: number) => {
                                              const isNavExpanded = expandedNavUrl === c.company_url;
                                              return (
                                              <React.Fragment key={c.company_url ?? i}>
                                              <tr className={`border-b hover:bg-accent/20 ${isNavExpanded ? 'bg-indigo-50/40' : ''}`}>
                                                <td className="px-2 py-1.5">
                                                  <input type="checkbox" className="h-3 w-3 cursor-pointer"
                                                    checked={selectedCoCompanies.has(c.company_url)}
                                                    onChange={e => {
                                                      const s = new Set(selectedCoCompanies);
                                                      e.target.checked ? s.add(c.company_url) : s.delete(c.company_url);
                                                      setSelectedCoCompanies(s);
                                                    }}
                                                  />
                                                </td>
                                                <td className="px-2 py-1.5 max-w-[240px]">
                                                  <div className="truncate font-medium">{c.company_name ?? '—'}</div>
                                                  <div className="text-[10px] text-muted-foreground truncate">{c.company_url}</div>
                                                </td>
                                                <td className="px-2 py-1.5 text-center">{c.profile_status === 'completed' ? '✅ 完成' : c.profile_status === 'failed' ? '❌ 失敗' : c.profile_status === 'pending' ? '⏳ 未爬' : '○'}</td>
                                                <td className="px-2 py-1.5 text-center">{c.is_factory ? '🏭' : c.nav_parsed ? '✅' : '—'}</td>
                                                <td className="px-2 py-1.5 text-center font-mono text-yellow-600">{c.rating ?? '—'}</td>
                                                <td className="px-2 py-1.5 text-center font-mono text-blue-600">{c.cert_count > 0 ? c.cert_count : '—'}</td>
                                                <td className="px-2 py-1.5 text-center">
                                                  {c.nav_category_count > 0 ? (
                                                    <button
                                                      onClick={() => setExpandedNavUrl(isNavExpanded ? null : c.company_url)}
                                                      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border transition-colors ${isNavExpanded ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'}`}
                                                      title="點擊展開 Nav 分類清單"
                                                    >
                                                      {c.nav_category_count} 分類{isNavExpanded ? ' ▲' : ' ▼'}
                                                    </button>
                                                  ) : (
                                                    <span className="text-gray-300">—</span>
                                                  )}
                                                </td>
                                              </tr>
                                              {/* Nav categories expanded row */}
                                              {isNavExpanded && (
                                                <tr className="border-b bg-indigo-50/30">
                                                  <td colSpan={7} className="px-4 py-2">
                                                    {navCategoriesFetching && expandedNavUrl === c.company_url ? (
                                                      <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                        <span>載入 Nav 分類中…</span>
                                                      </div>
                                                    ) : (navCategoriesData && navCategoriesData.length > 0) ? (
                                                      <div>
                                                        <div className="text-[10px] font-semibold text-indigo-700 mb-1.5">
                                                          Nav 分類清單 Nav Categories ({navCategoriesData.length} 個)
                                                        </div>
                                                        <div className="flex flex-wrap gap-1">
                                                          {navCategoriesData.slice(0, 10).map((cat, ci) => (
                                                            <span
                                                              key={ci}
                                                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border ${cat.nav_level === 0 ? 'bg-indigo-100 text-indigo-800 border-indigo-300 font-medium' : 'bg-white text-indigo-600 border-indigo-200'}`}
                                                              title={cat.category_url ?? undefined}
                                                            >
                                                              {cat.nav_level === 1 && <span className="mr-0.5 text-indigo-300">↳</span>}
                                                              {cat.category_name}
                                                            </span>
                                                          ))}
                                                          {navCategoriesData.length > 10 && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500 border border-gray-200">
                                                              +{navCategoriesData.length - 10} 更多
                                                            </span>
                                                          )}
                                                        </div>
                                                      </div>
                                                    ) : (
                                                      <span className="text-[10px] text-muted-foreground">無 Nav 分類資料</span>
                                                    )}
                                                  </td>
                                                </tr>
                                              )}
                                              </React.Fragment>
                                            );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
          )}

          {/* ── Ali PD sub-tab：Alibaba 原生矩陣（alibaba_searches 為基礎）── */}
          {matrixMode === 'ali' && aliSubTab === 'pd' && (
            aliPdMatrixLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : aliPdMatrixRows.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                尚無 Alibaba 搜尋資料（請先至 /alibaba-search 搜尋關鍵字）
              </div>
            ) : (
              <>
                {selectedKeywords.size > 0 && selectedPdSteps.size > 0 && (
                  <div className="px-3 py-2 border-b bg-blue-50/50 dark:bg-blue-900/10 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      已選 {selectedKeywords.size} 個關鍵字 × {selectedPdSteps.size} 個步驟
                    </span>
                    <button
                      className="px-2 py-0.5 rounded text-xs bg-blue-500 text-white hover:bg-blue-600"
                      onClick={() => {
                        // TODO: POST /pipeline/retrigger-ali-stages
                        toast.info(`將補跑 ${selectedKeywords.size} 關鍵字 × PD-${Array.from(selectedPdSteps).map(i=>i+1).join(',')} 步驟`);
                      }}
                    >
                      補跑 Ali {selectedKeywords.size}筆×{selectedPdSteps.size}步🔵
                    </button>
                  </div>
                )}
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="px-2 py-2 w-8 sticky left-0 z-10 bg-muted/50">
                        <input type="checkbox"
                          checked={aliPdMatrixRows.length > 0 && selectedKeywords.size === aliPdMatrixRows.length}
                          onChange={e => setSelectedKeywords(e.target.checked ? new Set(aliPdMatrixRows.map(r => r.keyword)) : new Set())}
                        />
                      </th>
                      <th className="text-left px-3 py-2 font-medium sticky left-8 bg-muted/50 min-w-[180px]">關鍵字</th>
                      <th className="px-2 py-1 text-center font-mono whitespace-nowrap min-w-[52px] text-muted-foreground">
                        <div>總計</div><div className="text-[9px] font-normal">商品數</div>
                      </th>
                      {ALI_PD_STEPS.map((step, idx) => (
                        <th key={step.label} className={`px-2 py-1 text-center font-mono whitespace-nowrap min-w-[52px] ${selectedPdSteps.has(idx) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                          <div className="flex items-center justify-center gap-1">
                            <input
                              type="checkbox"
                              className="h-3 w-3 cursor-pointer"
                              checked={selectedPdSteps.has(idx)}
                              onChange={e => {
                                const s = new Set(selectedPdSteps);
                                e.target.checked ? s.add(idx) : s.delete(idx);
                                setSelectedPdSteps(s);
                              }}
                              title={`選取 ${step.label} 欄`}
                            />
                            <span className="font-semibold">{step.label}</span>
                          </div>
                          <div className="text-[9px] text-muted-foreground font-normal">{step.sub}</div>
                        </th>
                      ))}
                      <th className="px-3 py-2 text-center font-mono whitespace-nowrap min-w-[60px]">
                        <div className="font-semibold text-blue-600">完成率</div>
                        <div className="text-[10px] text-muted-foreground font-normal">PD-2基準</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aliPdMatrixRows.map(row => {
                      const PD_DONE = [row.pd1_done, row.pd2_done, row.pd3_done, row.pd4_done, row.pd5_done,
                                       row.pd6_done, row.pd7_done, row.pd8_done, row.pd9_done, row.pd10_done]
                      return (
                        <React.Fragment key={row.keyword}>
                          <tr className={`border-b hover:bg-accent/30 ${aliPdPanel?.keyword === row.keyword ? 'bg-orange-50' : ''}`}>
                            <td className="px-2 py-1.5 w-8 sticky left-0 z-10 bg-background">
                              <input type="checkbox" checked={selectedKeywords.has(row.keyword)}
                                onChange={e => {
                                  const s = new Set(selectedKeywords)
                                  e.target.checked ? s.add(row.keyword) : s.delete(row.keyword)
                                  setSelectedKeywords(s)
                                }} />
                            </td>
                            <td
                              className="px-3 py-1.5 text-xs font-medium sticky left-8 bg-background truncate max-w-[180px] cursor-pointer hover:text-orange-600"
                              title={row.keyword}
                              onClick={() => {
                                setAliPdPanel(prev => prev?.keyword === row.keyword ? null : { keyword: row.keyword })
                                setAliPdFilter('all')
                                setAliPdOffset(0)
                              }}
                            >{row.keyword}</td>
                            <td className="px-2 py-1.5 text-center">
                              <span className="text-muted-foreground font-mono">{row.total_products}</span>
                            </td>
                            {PD_DONE.map((done, i) => {
                              const pct = row.total_products > 0 ? done / row.total_products : -1
                              const cls = pct < 0 ? 'text-gray-300' : pct >= 0.8 ? 'text-green-600' : pct >= 0.5 ? 'text-yellow-600' : pct > 0 ? 'text-red-500' : 'text-gray-400'
                              return (
                                <td key={i} className="px-2 py-1.5 text-center cursor-default"
                                  onMouseEnter={e => {
                                    if (hoverPdTimerRef.current) clearTimeout(hoverPdTimerRef.current);
                                    setHoverPdCell({ keyword: row.keyword, pdStep: i + 1, x: e.clientX, y: e.clientY });
                                  }}
                                  onMouseLeave={() => {
                                    hoverPdTimerRef.current = setTimeout(() => setHoverPdCell(null), 200);
                                  }}
                                >
                                  <span className={`text-[11px] font-mono ${cls}`}>
                                    {row.total_products === 0 ? '—' : `${done}/${row.total_products}`}
                                  </span>
                                </td>
                              )
                            })}
                            <td className="px-3 py-1.5 text-center">
                              {row.completion_pct != null ? (
                                <span className={`text-[12px] font-bold ${row.completion_pct >= 80 ? 'text-green-600' : row.completion_pct >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
                                  {row.completion_pct}%
                                </span>
                              ) : <span className="text-gray-300 text-[11px]">—</span>}
                            </td>
                          </tr>
                          {/* PD inline 展開 */}
                          {aliPdPanel?.keyword === row.keyword && (
                            <tr>
                              <td colSpan={ALI_PD_STEPS.length + 4} className="p-0 bg-orange-50/20 border-b">
                                <div className="p-3 max-h-[500px] overflow-y-auto">
                                  {/* Header */}
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <span className="font-semibold text-sm">{aliPdPanel.keyword}</span>
                                      <span className="text-xs text-muted-foreground ml-2">Alibaba Pipeline 進度</span>
                                      {aliPdProductsData && <span className="ml-1 text-blue-600 text-xs">（{aliPdProductsData.total_products} 筆 URL）</span>}
                                    </div>
                                    <button onClick={() => setAliPdPanel(null)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground">✕</button>
                                  </div>
                                  {/* 篩選 bar + 補跑按鈕 */}
                                  <div className="flex gap-1 py-2 flex-wrap">
                                    {(['all', 'failed', 'pending', 'not_started'] as const).map(f => (
                                      <button key={f}
                                        onClick={() => { setAliPdFilter(f); setAliPdOffset(0); }}
                                        className={`px-2.5 py-1 rounded text-xs transition-colors ${aliPdFilter === f ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>
                                        {f === 'all' ? '全部' : f === 'failed' ? '❌ 失敗' : f === 'pending' ? '⏱️ 等待' : '○ 未開始'}
                                      </button>
                                    ))}
                                    <div className="flex-1" />
                                    <button
                                      disabled={isRetriggeringAli}
                                      onClick={async () => {
                                        if (!aliPdPanel || !aliPdData) return;
                                        const stagesToRetrigger = new Set<string>();
                                        aliPdData.urls.forEach(item => {
                                          const relevantSteps = aliPdFilter === 'failed'
                                            ? item.steps.filter(s => s.status === 'failed')
                                            : aliPdFilter === 'pending'
                                            ? item.steps.filter(s => s.status === 'pending' || s.status === 'running')
                                            : aliPdFilter === 'not_started'
                                            ? item.steps.filter(s => !s.status || s.status === 'not_started')
                                            : item.steps.filter(s => s.status === 'failed' || s.status === 'pending');
                                          relevantSteps.forEach(s => stagesToRetrigger.add(s.step_id));
                                        });
                                        if (stagesToRetrigger.size === 0) { toast.info('沒有可補跑的步驟'); return; }
                                        setIsRetriggeringAli(true);
                                        try {
                                          const res = await alibabaApi.retrigerAliStages([aliPdPanel.keyword], Array.from(stagesToRetrigger));
                                          toast.success(`已重置 ${res.data?.reset ?? 0} 個步驟（${res.data?.task_count ?? 0} 個任務）`);
                                          queryClient.invalidateQueries({ queryKey: ['ali-pd-panel', aliPdPanel.keyword] });
                                        } catch (err: unknown) {
                                          toast.error(`補跑失敗: ${err instanceof Error ? err.message : String(err)}`);
                                        } finally {
                                          setIsRetriggeringAli(false);
                                        }
                                      }}
                                      className="px-2.5 py-1 rounded text-xs bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50">
                                      {isRetriggeringAli ? '補跑中…' : `補跑${aliPdFilter !== 'all' ? `（${aliPdFilter === 'failed' ? '失敗' : aliPdFilter === 'pending' ? '等待' : '未開始'}）` : ''}`}
                                    </button>
                                  </div>
                                  {/* Body — PD-1~PD-10 表格 */}
                                  <div className="overflow-x-auto">
                                    {aliPdProductsFetching && <div className="text-center py-8 text-muted-foreground text-sm">載入中…</div>}
                                    {!aliPdProductsFetching && (() => {
                                      const filtered = (aliPdProductsData?.data ?? []).filter(item => {
                                        if (aliPdFilter === 'all') return true;
                                        if (aliPdFilter === 'failed') return item.task_status === 'failed' || item.parse_status === 'failed';
                                        if (aliPdFilter === 'pending') return item.task_status === 'pending' || item.task_status === 'running';
                                        return !item.task_status;
                                      });
                                      if (filtered.length === 0 && aliPdProductsData) {
                                        return <div className="py-4 text-center text-xs text-muted-foreground">篩選條件無結果</div>;
                                      }
                                      return (
                                        <table className="w-full text-xs border-collapse">
                                          <thead>
                                            <tr className="bg-muted/50 border-b">
                                              <th className="text-left px-2 py-1.5 font-medium sticky left-0 bg-muted/50 min-w-[180px]">商品標題 / URL</th>
                                              {ALI_PD_STEPS.map(step => (
                                                <th key={step.label} className="px-2 py-1.5 text-center font-mono whitespace-nowrap min-w-[48px]">
                                                  <div className="font-semibold">{step.label}</div>
                                                  <div className="text-[9px] text-muted-foreground font-normal">{step.sub}</div>
                                                </th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {filtered.map((item, idx) => (
                                              <tr key={item.id ?? item.url} className="border-b hover:bg-accent/20">
                                                <td className="px-2 py-1.5 sticky left-0 bg-background">
                                                  <div className="font-medium truncate max-w-[180px]" title={item.title ?? item.url ?? ''}>
                                                    {item.title ?? <span className="text-muted-foreground italic">#{aliPdOffset + idx + 1}</span>}
                                                  </div>
                                                  {item.company && <div className="text-[10px] text-muted-foreground truncate">{item.company}</div>}
                                                </td>
                                                {ALI_PD_STEPS.map(step => (
                                                  <td key={step.label} className="px-2 py-1.5 text-center">
                                                    <PdStepIcon status={getPdStepStatus(item, step.dbId)} />
                                                  </td>
                                                ))}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      );
                                    })()}
                                  </div>
                                  {/* 分頁 + iframe 補跑 */}
                                  <div className="pt-3 text-xs flex items-center justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <span className="text-muted-foreground">
                                        共 {aliPdProductsData?.total_products ?? 0} 筆 URL
                                        {aliPdFilter !== 'all' && <span className="ml-1">（篩選中）</span>}
                                      </span>
                                      <button
                                        disabled={isIframeFetching}
                                        onClick={async () => {
                                          setIsIframeFetching(true);
                                          try {
                                            await alibabaApi.triggerIframeFetch();
                                            toast.success('Batch C-2 iframe fetch 已啟動（背景執行）');
                                          } catch { toast.error('啟動 iframe fetch 失敗'); }
                                          finally { setIsIframeFetching(false); }
                                        }}
                                        className="px-2 py-0.5 rounded border text-[10px] bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 disabled:opacity-50 whitespace-nowrap"
                                      >
                                        {isIframeFetching ? '啟動中…' : '↺ 補跑 iframe（全批）'}
                                      </button>
                                      <button
                                        disabled={isBackfillingC4}
                                        onClick={async () => {
                                          setIsBackfillingC4(true);
                                          try {
                                            const res = await alibabaApi.backfillC4(10);
                                            toast.success(`C-4 補觸發：${res.data.triggered} 筆`);
                                          } catch { toast.error('補觸發 C-4 失敗'); }
                                          finally { setIsBackfillingC4(false); }
                                        }}
                                        className="px-2 py-0.5 rounded border text-[10px] bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 disabled:opacity-50 whitespace-nowrap"
                                      >
                                        {isBackfillingC4 ? '觸發中…' : '↺ 補觸發 C-4 截圖（10 筆）'}
                                      </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        disabled={aliPdOffset === 0}
                                        onClick={() => setAliPdOffset(Math.max(0, aliPdOffset - ALI_PD_LIMIT))}
                                        className="px-2 py-0.5 rounded bg-muted disabled:opacity-40">◀</button>
                                      <span className="font-mono">
                                        {Math.floor(aliPdOffset / ALI_PD_LIMIT) + 1} / {Math.max(1, Math.ceil((aliPdProductsData?.total_products ?? 0) / ALI_PD_LIMIT))}
                                      </span>
                                      <button
                                        disabled={(aliPdOffset + ALI_PD_LIMIT) >= (aliPdProductsData?.total_products ?? 0)}
                                        onClick={() => setAliPdOffset(aliPdOffset + ALI_PD_LIMIT)}
                                        className="px-2 py-0.5 rounded bg-muted disabled:opacity-40">▶</button>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )
          )}

          {/* ── SERP 矩陣（非 ali 模式）── */}
          {matrixMode !== 'ali' && (matrixLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : matrixRows.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">無資料（SERP 尚未建立或篩選條件無結果）</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="px-2 py-2 w-8 sticky left-0 z-10 bg-muted/50">
                      <input type="checkbox"
                        checked={sortedRows.length > 0 && selectedKeywords.size === sortedRows.length}
                        onChange={e => setSelectedKeywords(e.target.checked ? new Set(sortedRows.map(r => r.keyword)) : new Set())} />
                    </th>
                    <th
                      className="text-left px-3 py-2 font-medium sticky left-8 bg-muted/50 min-w-[180px] cursor-pointer select-none hover:bg-muted/80"
                      onClick={() => { if (mSortBy === 'keyword') setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setMSortBy('keyword'); setSortDir('asc'); } }}
                    >
                      關鍵字 {mSortBy === 'keyword' && (sortDir === 'asc' ? '↑' : '↓')}
                    </th>
                    {visibleCols.map(col => {
                      const colKey = col.label.toLowerCase().replace('-', '') as SortKey;
                      const isSerpSortable = col.mode === 'serp';
                      const isActive = mSortBy === colKey;
                      const serpStageKey = ({'A-1':'a1','A-2':'a2','A-3':'a3'} as Record<string,'a1'|'a2'|'a3'>)[col.label];
                      const isAliStage = col.mode === 'ali';
                      const isStageSelected = serpStageKey
                        ? selectedStages.has(serpStageKey)
                        : (isAliStage ? selectedAliStages.has(col.label) : false);
                      return (
                        <th
                          key={col.label}
                          title={col.desc}
                          className={`px-2 py-1 text-center font-mono whitespace-nowrap min-w-[80px] select-none ${isSerpSortable ? 'cursor-pointer hover:bg-muted/80' : 'cursor-help'} ${isStageSelected && serpStageKey ? 'bg-orange-50' : isStageSelected && isAliStage ? 'bg-blue-50' : ''}`}
                          onClick={() => {
                            if (!isSerpSortable) return;
                            if (isActive) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                            else { setMSortBy(colKey); setSortDir('desc'); }
                          }}
                        >
                          {(serpStageKey || isAliStage) && (
                            <div className="flex justify-center mb-0.5" onClick={e => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                title={serpStageKey ? `勾選 ${col.label} 列入立刻處理` : `勾選 ${col.label} 列入補跑`}
                                checked={isStageSelected}
                                className={`h-3 w-3 cursor-pointer ${serpStageKey ? 'accent-orange-500' : 'accent-blue-500'}`}
                                onChange={e => {
                                  if (serpStageKey) {
                                    const s = new Set(selectedStages);
                                    e.target.checked ? s.add(serpStageKey) : s.delete(serpStageKey);
                                    setSelectedStages(s);
                                  } else {
                                    const s = new Set(selectedAliStages);
                                    e.target.checked ? s.add(col.label) : s.delete(col.label);
                                    setSelectedAliStages(s);
                                  }
                                }}
                              />
                            </div>
                          )}
                          <div className="font-semibold flex items-center justify-center gap-0.5">
                            {col.label}
                            {isActive && <span className="text-[9px]">{sortDir === 'desc' ? '↓' : '↑'}</span>}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-normal">{col.sub}</div>
                        </th>
                      );
                    })}
                    {/* SERP-LLM mode: per-tab readiness columns */}
                    {(matrixMode === 'serp-llm' || matrixMode === 'all') && ([
                      { key: 'serp',    label: '🔍SERP',   sub: '結果',   tip: 'DataForSEO 有幾筆結果',            color: 'text-blue-600' },
                      { key: 'analysis',label: '📊分析',   sub: 'LLM步驟',tip: 'SERP LLM 分析步驟 (Step3~6)',     color: 'text-purple-600' },
                      { key: 'htags',   label: 'H標籤',    sub: 'T10/T20', tip: 'TOP10≥5 AND TOP20≥10 H標籤門檻',  color: 'text-green-600' },
                      { key: 'nav',     label: '🧭導覽',   sub: 'Nav',    tip: 'TOP20 有 nav_tree_json 的頁數',    color: 'text-teal-600' },
                      { key: 'seo',     label: '📝SEO',    sub: 'Details', tip: 'TOP20 有 seo_analysis 的頁數',    color: 'text-orange-600' },
                      { key: 'paa',     label: '❓PAA',    sub: 'Questions',tip: 'People Also Ask 問題數',         color: 'text-yellow-600' },
                      { key: 'ngram',   label: 'N-gram',   sub: 'Ngram',  tip: 'Ngram 分析筆數',                  color: 'text-indigo-600' },
                      { key: 'rank',    label: '📈排名',   sub: 'Trends', tip: '排名快照筆數 (rank_snapshots)',    color: 'text-pink-600' },
                    ] as const).map(col => (
                      <th key={col.key}
                        className={`px-1.5 py-1 text-center font-mono whitespace-nowrap min-w-[52px] cursor-help`}>
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <div className={`font-semibold text-[10px] ${col.color}`}>{col.label}</div>
                                <div className="text-[9px] text-muted-foreground font-normal">{col.sub}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[200px] text-xs">
                              {col.tip}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                    ))}
                    <th
                      className="px-3 py-2 text-center font-mono whitespace-nowrap min-w-[60px] cursor-pointer select-none hover:bg-muted/80"
                      title={matrixMode === 'ali' ? 'A4~C5 十步驟 Alibaba 完成率（TOP+非TOP 合計）' : 'A-1+A-2+A-3 綜合完成率（TOP+非TOP 合計）'}
                      onClick={() => { if (mSortBy === 'completion') setSortDir(d => d === 'desc' ? 'asc' : 'desc'); else { setMSortBy('completion'); setSortDir('desc'); } }}
                    >
                      <div className="font-semibold text-blue-600 flex items-center justify-center gap-0.5">
                        完成率 {mSortBy === 'completion' && (sortDir === 'desc' ? '↓' : '↑')}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-normal">{matrixMode === 'ali' ? 'A4~C5' : 'A1~A3'}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map(row => (
                    <React.Fragment key={row.keyword}>
                    <tr className="border-b hover:bg-accent/30">
                      <td className="px-2 py-1.5 w-8 sticky left-0 z-10 bg-background">
                        <input type="checkbox" checked={selectedKeywords.has(row.keyword)}
                          onChange={e => {
                            const s = new Set(selectedKeywords);
                            e.target.checked ? s.add(row.keyword) : s.delete(row.keyword);
                            setSelectedKeywords(s);
                          }} />
                      </td>
                      <td
                        className={`px-3 py-1.5 text-xs font-medium sticky left-8 bg-background truncate max-w-[180px] cursor-pointer ${matrixMode === 'ali' ? 'hover:text-orange-600' : 'hover:text-blue-600'} ${selectedSerpKeyword === row.keyword ? '!bg-blue-50' : ''} ${selectedA4Keyword?.keyword === row.keyword ? '!bg-indigo-50' : ''} ${matrixMode === 'ali' && aliPdPanel?.keyword === row.keyword ? '!bg-orange-50' : ''}`}
                        title={row.keyword}
                        onClick={() => {
                          if (matrixMode === 'serp') {
                            setSelectedSerpKeyword(prev => prev === row.keyword ? null : row.keyword);
                          } else if (matrixMode === 'serp-llm' || matrixMode === 'all') {
                            const sa = serpAnalysisMap.get(row.keyword);
                            if (sa) {
                              setSelectedA4Keyword(prev =>
                                prev?.keyword === row.keyword ? null : { keyword: row.keyword, requestId: sa.request_id }
                              );
                            }
                          } else if (matrixMode === 'ali') {
                            setAliPdPanel(prev => prev?.keyword === row.keyword ? null : { keyword: row.keyword });
                            setAliPdFilter('all');
                            setAliPdOffset(0);
                          }
                        }}
                      >{row.keyword}</td>
                      {visibleCols.map(col => {
                        const stageMap: Record<string, 'a1'|'a2'|'a3'> = {'A-1':'a1','A-2':'a2','A-3':'a3'};
                        const stage = stageMap[col.label];
                        const isAliStage = col.mode === 'ali';
                        return (
                          <td
                            key={col.label}
                            className={`px-2 py-1.5 text-center ${(stage || isAliStage) ? 'cursor-pointer hover:bg-accent/50' : ''}`}
                            onMouseEnter={(stage || isAliStage) ? (e) => {
                              if (stage) {
                                if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                hoverTimerRef.current = setTimeout(() => {
                                  setHoverCell({ keyword: row.keyword, stage, x: rect.right, y: rect.top });
                                }, 350);
                              } else if (isAliStage) {
                                if (hoverAliTimerRef.current) clearTimeout(hoverAliTimerRef.current);
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                hoverAliTimerRef.current = setTimeout(() => {
                                  setHoverAliCell({ keyword: row.keyword, stepId: col.label, x: rect.right, y: rect.top });
                                }, 350);
                              }
                            } : undefined}
                            onMouseLeave={(stage || isAliStage) ? () => {
                              if (stage) {
                                if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                                hoverTimerRef.current = setTimeout(() => setHoverCell(null), 150);
                              } else if (isAliStage) {
                                if (hoverAliTimerRef.current) clearTimeout(hoverAliTimerRef.current);
                                hoverAliTimerRef.current = setTimeout(() => setHoverAliCell(null), 150);
                              }
                            } : undefined}
                            onClick={() => {
                              if (stage) {
                                setCellDetail({ keyword: row.keyword, stage, label: `${col.label} ${col.sub}` });
                              } else if (isAliStage) {
                                setAliPdPanel({ keyword: row.keyword });
                                setAliPdFilter('all');
                                setAliPdOffset(0);
                              }
                            }}
                          >
                            <MatrixCell
                              topDone={row[col.topKey] as number}
                              topTotal={row.top_total}
                              nonDone={row[col.nonKey] as number}
                              nonTotal={row.non_top_total}
                              combined={col.mode === 'ali'}
                            />
                          </td>
                        );
                      })}
                      {/* A-4 SERP analysis cell — single summary (serp mode only) */}
                      {matrixMode === 'serp' && (
                        <td
                          className="px-2 py-1.5 text-center cursor-pointer hover:bg-accent/50"
                          title="點擊查看完整 SERP 分析報告"
                          onClick={() => {
                            const item = serpAnalysisMap.get(row.keyword);
                            if (item) navigate(`/analysis-reports/${item.request_id}`);
                          }}
                        >
                          <SerpAnalysisCell item={serpAnalysisMap.get(row.keyword)} />
                        </td>
                      )}
                      {/* 8 per-tab readiness cells (serp-llm / all mode) */}
                      {(matrixMode === 'serp-llm' || matrixMode === 'all') && (() => {
                        const sa = serpAnalysisMap.get(row.keyword);
                        const go = (id: string) => navigate(`/analysis-reports/${id}`);
                        const cells: { key: string; content: ReactNode; tip: string }[] = [
                          {
                            key: 'serp',
                            content: row.serp_result_count > 0
                              ? <span className="text-green-600 text-[11px] font-mono">✅ {row.serp_result_count}</span>
                              : <span className="text-gray-300 text-[11px]">—</span>,
                            tip: `DataForSEO 結果: ${row.serp_result_count} 筆`,
                          },
                          {
                            key: 'analysis',
                            content: <SerpAnalysisCell item={sa} />,
                            tip: sa
                              ? `已解析: ${sa.parsed_htags}/10 | 步驟:${sa.steps_completed}/${sa.steps_total}`
                              : '尚無分析記錄',
                          },
                          {
                            key: 'htags',
                            content: sa
                              ? <span className={`text-[11px] font-mono ${sa.analysis_ready ? 'text-green-600' : 'text-orange-500'}`}>
                                  {sa.analysis_ready ? '✅' : '⚠️'} {sa.parsed_htags}/10
                                </span>
                              : <span className="text-gray-300 text-[11px]">—</span>,
                            tip: sa
                              ? `H標籤已解析: ${sa.parsed_htags}/10 ${sa.analysis_ready ? '✅達標' : '⚠️未達標'}`
                              : 'H標籤資料未抓取',
                          },
                          {
                            key: 'nav',
                            content: row.nav_top_count > 0
                              ? <span className="text-teal-600 text-[11px] font-mono">✅ {row.nav_top_count}</span>
                              : <span className="text-gray-300 text-[11px]">0</span>,
                            tip: `nav_tree_json: TOP20 中 ${row.nav_top_count} 頁有導覽列`,
                          },
                          {
                            key: 'seo',
                            content: row.seo_top_count > 0
                              ? <span className="text-orange-600 text-[11px] font-mono">✅ {row.seo_top_count}</span>
                              : <span className="text-gray-300 text-[11px]">0</span>,
                            tip: `seo_analysis: TOP20 中 ${row.seo_top_count} 頁已分析`,
                          },
                          {
                            key: 'paa',
                            content: row.paa_count > 0
                              ? <span className="text-yellow-700 text-[11px] font-mono">✅ {row.paa_count}</span>
                              : <span className="text-gray-300 text-[11px]">0</span>,
                            tip: `People Also Ask: ${row.paa_count} 個問題`,
                          },
                          {
                            key: 'ngram',
                            content: row.ngram_count > 0
                              ? <span className="text-indigo-600 text-[11px] font-mono">✅ {row.ngram_count}</span>
                              : <span className="text-gray-300 text-[11px]">0</span>,
                            tip: `Ngram 分析: ${row.ngram_count} 筆`,
                          },
                          {
                            key: 'rank',
                            content: row.rank_count > 0
                              ? <span className="text-pink-600 text-[11px] font-mono">✅ {row.rank_count}</span>
                              : <span className="text-gray-300 text-[11px]">0</span>,
                            tip: `排名快照: ${row.rank_count} 筆`,
                          },
                        ];
                        return cells.map(cell => (
                          <td
                            key={cell.key}
                            className="px-1.5 py-1.5 text-center cursor-pointer hover:bg-accent/50"
                            onClick={() => { if (sa) go(sa.request_id); }}
                          >
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-block">{cell.content}</span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[220px] text-xs leading-snug">
                                  {cell.tip}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </td>
                        ));
                      })()}
                      <td className="px-3 py-1.5 text-center">
                        {(() => {
                          const pct = matrixMode === 'ali' ? getAliPct(row) : row.completion_pct;
                          return pct != null ? (
                            <span className={`text-[12px] font-bold ${pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-yellow-600' : 'text-red-500'}`}>
                              {pct}%
                            </span>
                          ) : <span className="text-gray-300 text-[11px]">—</span>;
                        })()}
                      </td>
                    </tr>
                    {/* PD inline 展開（ali mode 下點關鍵字列後出現） */}
                    {matrixMode === 'ali' && aliPdPanel?.keyword === row.keyword && (
                      <tr>
                        <td colSpan={visibleCols.length + 3} className="p-0 bg-orange-50/20 border-b">
                          <div className="p-3 max-h-[500px] overflow-y-auto">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <span className="font-semibold text-sm">{aliPdPanel.keyword}</span>
                                <span className="text-xs text-muted-foreground ml-2">Alibaba Pipeline 進度</span>
                                {aliPdProductsData && <span className="ml-1 text-blue-600 text-xs">（{aliPdProductsData.total_products} 筆 URL）</span>}
                              </div>
                              <button onClick={() => setAliPdPanel(null)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground">✕</button>
                            </div>
                            {/* 篩選 bar + 補跑按鈕 */}
                            <div className="flex gap-1 py-2 flex-wrap">
                              {(['all', 'failed', 'pending', 'not_started'] as const).map(f => (
                                <button key={f}
                                  onClick={() => { setAliPdFilter(f); setAliPdOffset(0); }}
                                  className={`px-2.5 py-1 rounded text-xs transition-colors ${aliPdFilter === f ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>
                                  {f === 'all' ? '全部' : f === 'failed' ? '❌ 失敗' : f === 'pending' ? '⏱️ 等待' : '○ 未開始'}
                                </button>
                              ))}
                              <div className="flex-1" />
                              <button
                                disabled={isRetriggeringAli}
                                onClick={async () => {
                                  if (!aliPdPanel || !aliPdData) return;
                                  const stagesToRetrigger = new Set<string>();
                                  aliPdData.urls.forEach(item => {
                                    const relevantSteps = aliPdFilter === 'failed'
                                      ? item.steps.filter(s => s.status === 'failed')
                                      : aliPdFilter === 'pending'
                                      ? item.steps.filter(s => s.status === 'pending' || s.status === 'running')
                                      : aliPdFilter === 'not_started'
                                      ? item.steps.filter(s => !s.status || s.status === 'not_started')
                                      : item.steps.filter(s => s.status === 'failed' || s.status === 'pending');
                                    relevantSteps.forEach(s => stagesToRetrigger.add(s.step_id));
                                  });
                                  if (stagesToRetrigger.size === 0) { toast.info('沒有可補跑的步驟'); return; }
                                  setIsRetriggeringAli(true);
                                  try {
                                    const res = await alibabaApi.retrigerAliStages([aliPdPanel.keyword], Array.from(stagesToRetrigger));
                                    toast.success(`已重置 ${res.data?.reset ?? 0} 個步驟（${res.data?.task_count ?? 0} 個任務）`);
                                    queryClient.invalidateQueries({ queryKey: ['ali-pd-panel', aliPdPanel.keyword] });
                                  } catch (err: unknown) {
                                    toast.error(`補跑失敗: ${err instanceof Error ? err.message : String(err)}`);
                                  } finally {
                                    setIsRetriggeringAli(false);
                                  }
                                }}
                                className="px-2.5 py-1 rounded text-xs bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50">
                                {isRetriggeringAli ? '補跑中…' : `補跑${aliPdFilter !== 'all' ? `（${aliPdFilter === 'failed' ? '失敗' : aliPdFilter === 'pending' ? '等待' : '未開始'}）` : ''}`}
                              </button>
                            </div>
                            {/* Body — PD-1~PD-10 表格 */}
                            <div className="overflow-x-auto">
                              {aliPdProductsFetching && <div className="text-center py-8 text-muted-foreground text-sm">載入中…</div>}
                              {!aliPdProductsFetching && (() => {
                                const filtered = (aliPdProductsData?.data ?? []).filter(item => {
                                  if (aliPdFilter === 'all') return true;
                                  if (aliPdFilter === 'failed') return item.task_status === 'failed' || item.parse_status === 'failed';
                                  if (aliPdFilter === 'pending') return item.task_status === 'pending' || item.task_status === 'running';
                                  return !item.task_status;
                                });
                                if (filtered.length === 0 && aliPdProductsData) {
                                  return <div className="py-4 text-center text-xs text-muted-foreground">篩選條件無結果</div>;
                                }
                                return (
                                  <table className="w-full text-xs border-collapse">
                                    <thead>
                                      <tr className="bg-muted/50 border-b">
                                        <th className="text-left px-2 py-1.5 font-medium sticky left-0 bg-muted/50 min-w-[180px]">商品標題 / URL</th>
                                        {ALI_PD_STEPS.map(step => (
                                          <th key={step.label} className="px-2 py-1.5 text-center font-mono whitespace-nowrap min-w-[48px]">
                                            <div className="font-semibold">{step.label}</div>
                                            <div className="text-[9px] text-muted-foreground font-normal">{step.sub}</div>
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {filtered.map((item, idx) => (
                                        <tr key={item.id ?? item.url} className="border-b hover:bg-accent/20">
                                          <td className="px-2 py-1.5 sticky left-0 bg-background">
                                            <div className="font-medium truncate max-w-[180px]" title={item.title ?? item.url ?? ''}>
                                              {item.title ?? <span className="text-muted-foreground italic">#{aliPdOffset + idx + 1}</span>}
                                            </div>
                                            {item.company && <div className="text-[10px] text-muted-foreground truncate">{item.company}</div>}
                                          </td>
                                          {ALI_PD_STEPS.map(step => (
                                            <td key={step.label} className="px-2 py-1.5 text-center">
                                              <PdStepIcon status={getPdStepStatus(item, step.dbId)} />
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                );
                              })()}
                            </div>
                            {/* 分頁 + iframe 補跑 */}
                            <div className="pt-3 text-xs flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">
                                  共 {aliPdProductsData?.total_products ?? 0} 筆 URL
                                  {aliPdFilter !== 'all' && <span className="ml-1">（篩選中）</span>}
                                </span>
                                <button
                                  disabled={isIframeFetching}
                                  onClick={async () => {
                                    setIsIframeFetching(true);
                                    try {
                                      await alibabaApi.triggerIframeFetch();
                                      toast.success('Batch C-2 iframe fetch 已啟動（背景執行）');
                                    } catch { toast.error('啟動 iframe fetch 失敗'); }
                                    finally { setIsIframeFetching(false); }
                                  }}
                                  className="px-2 py-0.5 rounded border text-[10px] bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {isIframeFetching ? '啟動中…' : '↺ 補跑 iframe（全批）'}
                                </button>
                                <button
                                  disabled={isBackfillingC4}
                                  onClick={async () => {
                                    setIsBackfillingC4(true);
                                    try {
                                      const res = await alibabaApi.backfillC4(10);
                                      toast.success(`C-4 補觸發：${res.data.triggered} 筆`);
                                    } catch { toast.error('補觸發 C-4 失敗'); }
                                    finally { setIsBackfillingC4(false); }
                                  }}
                                  className="px-2 py-0.5 rounded border text-[10px] bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {isBackfillingC4 ? '觸發中…' : '↺ 補觸發 C-4 截圖（10 筆）'}
                                </button>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  disabled={aliPdOffset === 0}
                                  onClick={() => setAliPdOffset(Math.max(0, aliPdOffset - ALI_PD_LIMIT))}
                                  className="px-2 py-0.5 rounded bg-muted disabled:opacity-40">◀</button>
                                <span className="font-mono">
                                  {Math.floor(aliPdOffset / ALI_PD_LIMIT) + 1} / {Math.max(1, Math.ceil((aliPdProductsData?.total_products ?? 0) / ALI_PD_LIMIT))}
                                </span>
                                <button
                                  disabled={(aliPdOffset + ALI_PD_LIMIT) >= (aliPdProductsData?.total_products ?? 0)}
                                  onClick={() => setAliPdOffset(aliPdOffset + ALI_PD_LIMIT)}
                                  className="px-2 py-0.5 rounded bg-muted disabled:opacity-40">▶</button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {/* Pagination */}
          {matrixTotal > mLimit && (
            <div className="flex items-center justify-center gap-2 py-2 border-t">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={mOffset === 0} onClick={() => setMOffset(Math.max(0, mOffset - mLimit))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground">第 {Math.floor(mOffset / mLimit) + 1} / {Math.ceil(matrixTotal / mLimit)} 頁</span>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={mOffset + mLimit >= matrixTotal} onClick={() => setMOffset(mOffset + mLimit)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Failed tasks */}
      {failedItems.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" /> 失敗步驟 ({failedItems.length})
              </CardTitle>
              <Button size="sm" variant="destructive" className="h-7 text-xs gap-1"
                disabled={retryingAll} onClick={handleRetryAll}>
                {retryingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                重新爬取全部 ({failedItems.length})
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-2 py-1">任務</th>
                  <th className="px-2 py-1">步驟</th>
                  <th className="px-2 py-1">錯誤</th>
                  <th className="px-2 py-1 w-16">操作</th>
                </tr>
              </thead>
              <tbody>
                {failedItems.map(({ taskId, step }) => (
                  <tr key={`${taskId}-${step.step_id}`} className="border-b hover:bg-accent/50">
                    <td className="px-2 py-1.5 font-mono text-xs">{taskId.slice(0, 12)}…</td>
                    <td className="px-2 py-1.5">
                      <Badge variant="outline" className="text-[11px]">{step.step_id}</Badge>
                      <span className="ml-1 text-xs">{step.step_name}</span>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-red-600 max-w-[300px] truncate">{step.error_message}</td>
                    <td className="px-2 py-1.5">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleRetry(taskId, step.step_id)}>
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Per-task timelines — hidden in Alibaba mode (not relevant to Ali SC/CO/PD) */}
      {matrixMode !== 'ali' && (
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base">各任務 Pipeline</CardTitle>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </CardHeader>
          <CardContent className="space-y-4">
            {overviewError && (
              <div className="text-sm text-destructive flex items-center gap-2 py-2">
                <span>⚠️ 資料載入失敗（DB 連線超時），請稍後再試</span>
              </div>
            )}
            {!isLoading && !overviewError && overview.length === 0 && (
              <div className="text-sm text-muted-foreground py-4 text-center">尚無 Pipeline 資料</div>
            )}
            {overview.map(task => (
              <div key={task.task_id} className="border rounded-lg">
                <button
                  className="w-full flex items-center justify-between px-4 py-2 hover:bg-accent/50 transition-colors"
                  onClick={() => setExpandedTask(prev => prev === task.task_id ? null : task.task_id)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{task.task_id.slice(0, 12)}…</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[300px]">{task.url}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">{task.completed}/{task.total}</span>
                    {task.failed > 0 && <Badge variant="destructive" className="text-[10px] px-1">{task.failed} fail</Badge>}
                  </div>
                </button>
                {expandedTask === task.task_id && (
                  <div className="border-t">
                    <PipelineTimeline steps={task.steps} onRetry={(stepId) => handleRetry(task.task_id, stepId)} />
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Cell drill-down modal */}
      {UrlDetailModal()}

      {/* Alibaba hover tooltip — pipeline step breakdown per task */}
      {hoverAliCell && (
        <div
          className="fixed z-50 bg-white border border-gray-200 shadow-2xl rounded-lg p-3 text-xs w-[420px] max-h-96 overflow-y-auto pointer-events-auto"
          style={{
            left: Math.min(hoverAliCell.x + 10, window.innerWidth - 440),
            top:  Math.max(hoverAliCell.y - 10, 10),
          }}
          onMouseEnter={() => { if (hoverAliTimerRef.current) clearTimeout(hoverAliTimerRef.current); }}
          onMouseLeave={() => { hoverAliTimerRef.current = setTimeout(() => setHoverAliCell(null), 150); }}
        >
          <div className="font-semibold text-[11px] text-gray-500 mb-2 pb-1 border-b flex items-center justify-between">
            <span className="truncate">{hoverAliCell.keyword}</span>
            <span className="text-blue-600 font-mono ml-2 shrink-0">{hoverAliCell.stepId}</span>
          </div>
          {hoverAliLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : !hoverAliData ? (
            <div className="text-center text-gray-400 py-4">無資料</div>
          ) : (
            <>
              {/* Summary counts */}
              <div className="flex flex-wrap gap-1.5 mb-2 pb-1.5 border-b border-gray-100">
                {hoverAliData.summary.completed     > 0 && <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[10px]">✅ 完成 {hoverAliData.summary.completed}</span>}
                {hoverAliData.summary.completed_empty > 0 && <span className="px-1.5 py-0.5 bg-gray-50 text-gray-500 rounded text-[10px]">✅ 空 {hoverAliData.summary.completed_empty}</span>}
                {hoverAliData.summary.failed        > 0 && <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-[10px]">❌ 失敗 {hoverAliData.summary.failed}</span>}
                {hoverAliData.summary.running       > 0 && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">🔄 執行中 {hoverAliData.summary.running}</span>}
                {hoverAliData.summary.pending       > 0 && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded text-[10px]">⏱️ 等待 {hoverAliData.summary.pending}</span>}
                {hoverAliData.summary.skipped       > 0 && <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-500 rounded text-[10px]">⏩ 跳過 {hoverAliData.summary.skipped}</span>}
                {hoverAliData.summary.not_applicable > 0 && <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 rounded text-[10px]">⛔ 不適用 {hoverAliData.summary.not_applicable}</span>}
                {hoverAliData.summary.not_started   > 0 && <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 rounded text-[10px]">○ 未開始 {hoverAliData.summary.not_started}</span>}
              </div>
              {/* Item list */}
              <div className="space-y-0.5">
                {hoverAliData.items.slice(0, 50).map(item => (
                  <div key={item.task_id} className="flex items-center gap-1.5 py-0.5 border-b border-gray-50 last:border-0">
                    {item.status === 'completed'       && <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />}
                    {item.status === 'completed_empty' && <CheckCircle2 className="h-3.5 w-3.5 text-gray-300 shrink-0" />}
                    {item.status === 'failed'          && <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                    {item.status === 'running'         && <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin shrink-0" />}
                    {item.status === 'pending'         && <Clock className="h-3.5 w-3.5 text-orange-400 shrink-0" />}
                    {item.status === 'skipped'         && <span className="text-[10px] text-indigo-400 shrink-0">⏩</span>}
                    {item.status === 'not_applicable'  && <span className="text-[10px] text-gray-300 shrink-0">⛔</span>}
                    {item.status === 'not_started'     && <span className="h-3.5 w-3.5 rounded-full border border-gray-300 shrink-0 inline-block" />}
                    <span className="text-gray-400 font-mono text-[10px] shrink-0">{item.task_id.slice(0, 8)}</span>
                    <span className="truncate text-gray-600 flex-1 min-w-0 text-[10px]" title={item.url}>
                      {item.url ? item.url.replace('https://www.alibaba.com/product-detail/', '').slice(0, 40) : '—'}
                    </span>
                    {item.error_message && (
                      <span className="text-red-400 text-[10px] shrink-0 truncate max-w-[100px]" title={item.error_message}>
                        {item.error_message.slice(0, 20)}
                      </span>
                    )}
                    {item.status === 'skipped' && (
                      <span className="text-indigo-300 text-[10px] shrink-0">A-4 完整</span>
                    )}
                    {item.status === 'not_applicable' && (
                      <span className="text-gray-300 text-[10px] shrink-0">無 iframe</span>
                    )}
                  </div>
                ))}
                {hoverAliData.total > 50 && (
                  <div className="text-center text-gray-400 text-[10px] pt-1">共 {hoverAliData.total} 筆，顯示前 50</div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* PD hover tooltip — per-step DB field values (ali-pd-step-tooltip endpoint) */}
      {hoverPdCell && (
        <div
          className="fixed z-50 bg-white border border-gray-200 shadow-2xl rounded-lg p-3 text-xs w-[460px] max-h-[480px] overflow-y-auto pointer-events-auto"
          style={{
            left: Math.min(hoverPdCell.x + 12, window.innerWidth - 480),
            top:  Math.max(hoverPdCell.y - 10, 10),
          }}
          onMouseEnter={() => { if (hoverPdTimerRef.current) clearTimeout(hoverPdTimerRef.current); }}
          onMouseLeave={() => { hoverPdTimerRef.current = setTimeout(() => setHoverPdCell(null), 200); }}
        >
          {/* Header */}
          <div className="font-semibold text-[11px] text-gray-500 mb-2 pb-1.5 border-b flex items-center justify-between gap-2">
            <span className="truncate flex-1 min-w-0">{hoverPdCell.keyword}</span>
            <span className="text-orange-600 font-mono shrink-0">PD-{hoverPdCell.pdStep}</span>
            {hoverPdData && <span className="text-blue-500 text-[10px] shrink-0 italic">{hoverPdData.field_label}</span>}
          </div>

          {hoverPdLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : !hoverPdData ? (
            <div className="text-center text-gray-400 py-4">無資料</div>
          ) : (
            <>
              {/* Summary chips */}
              <div className="flex flex-wrap gap-1.5 mb-2 pb-1.5 border-b border-gray-100">
                {(hoverPdData.summary.done ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[10px]">✅ 完成 {hoverPdData.summary.done}</span>
                )}
                {(hoverPdData.summary.partial ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded text-[10px]">⚠️ 部分 {hoverPdData.summary.partial}</span>
                )}
                {(hoverPdData.summary.failed ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-[10px]">❌ 失敗 {hoverPdData.summary.failed}</span>
                )}
                {(hoverPdData.summary.running ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">🔄 執行中 {hoverPdData.summary.running}</span>
                )}
                {(hoverPdData.summary.pending ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded text-[10px]">⏱️ 等待 {hoverPdData.summary.pending}</span>
                )}
                {(hoverPdData.summary.not_started ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-gray-50 text-gray-400 rounded text-[10px]">○ 未開始 {hoverPdData.summary.not_started}</span>
                )}
                <span className="ml-auto text-gray-400 text-[10px] shrink-0">共 {hoverPdData.total} 筆</span>
              </div>

              {/* Item list — title + field_label field_value + status */}
              <div className="space-y-0">
                {hoverPdData.items.slice(0, 50).map((item, idx) => (
                  <div key={idx} className="py-1 border-b border-gray-50 last:border-0">
                    {/* Row 1: status icon + product title */}
                    <div className="flex items-start gap-1.5">
                      {item.status === 'done'        && <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />}
                      {item.status === 'partial'     && <CheckCircle2 className="h-3.5 w-3.5 text-yellow-400 shrink-0 mt-0.5" />}
                      {item.status === 'failed'      && <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />}
                      {item.status === 'running'     && <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin shrink-0 mt-0.5" />}
                      {item.status === 'pending'     && <Clock className="h-3.5 w-3.5 text-orange-400 shrink-0 mt-0.5" />}
                      {item.status === 'not_started' && <span className="h-3.5 w-3.5 rounded-full border border-gray-300 shrink-0 inline-block mt-0.5" />}
                      <span className="text-gray-700 flex-1 min-w-0 leading-tight" title={item.url ?? undefined}>
                        {item.title || (item.url ? item.url.replace('https://www.alibaba.com/product-detail/', '').slice(0, 55) : '—')}
                      </span>
                    </div>
                    {/* Row 2: field label + field value */}
                    {item.field_value !== null && item.field_value !== undefined && (
                      <div className="ml-5 mt-0.5 flex items-start gap-1">
                        <span className="text-[9px] text-gray-400 shrink-0 uppercase tracking-wide pt-0.5">{hoverPdData.field_label}:</span>
                        <span className="text-[10px] text-blue-600 break-all leading-tight"
                          title={String(item.field_value)}>
                          {String(item.field_value).length > 80 ? String(item.field_value).slice(0, 80) + '…' : String(item.field_value)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                {hoverPdData.total > 50 && (
                  <div className="text-center text-gray-400 text-[10px] pt-1">顯示前 50 筆 / 共 {hoverPdData.total}</div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab3 Alibaba PD Panel — 已移至 PD 矩陣 tbody inline 展開 */}
      {false && aliPdPanel && (
        <div className="fixed inset-y-0 right-0 w-[480px] bg-background border-l shadow-xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div>
              <div className="font-semibold text-sm truncate max-w-[360px]">{aliPdPanel.keyword}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Alibaba Pipeline 進度
                {aliPdProductsData && <span className="ml-1 text-blue-600">（{aliPdProductsData.total_products} 筆 URL）</span>}
              </div>
            </div>
            <button onClick={() => setAliPdPanel(null)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground">✕</button>
          </div>

          {/* 篩選 bar + 補跑按鈕 */}
          <div className="flex gap-1 px-3 py-2 border-b bg-muted/10 flex-wrap">
            {(['all', 'failed', 'pending', 'not_started'] as const).map(f => (
              <button key={f}
                onClick={() => { setAliPdFilter(f); setAliPdOffset(0); }}
                className={`px-2.5 py-1 rounded text-xs transition-colors ${aliPdFilter === f ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>
                {f === 'all' ? '全部' : f === 'failed' ? '❌ 失敗' : f === 'pending' ? '⏱️ 等待' : '○ 未開始'}
              </button>
            ))}
            <div className="flex-1" />
            <button
              disabled={isRetriggeringAli}
              onClick={async () => {
                if (!aliPdPanel || !aliPdData) return;
                const stagesToRetrigger = new Set<string>();
                aliPdData.urls.forEach(item => {
                  const relevantSteps = aliPdFilter === 'failed'
                    ? item.steps.filter(s => s.status === 'failed')
                    : aliPdFilter === 'pending'
                    ? item.steps.filter(s => s.status === 'pending' || s.status === 'running')
                    : aliPdFilter === 'not_started'
                    ? item.steps.filter(s => !s.status || s.status === 'not_started')
                    : item.steps.filter(s => s.status === 'failed' || s.status === 'pending');
                  relevantSteps.forEach(s => stagesToRetrigger.add(s.step_id));
                });
                if (stagesToRetrigger.size === 0) { toast.info('沒有可補跑的步驟'); return; }
                setIsRetriggeringAli(true);
                try {
                  const res = await alibabaApi.retrigerAliStages([aliPdPanel.keyword], Array.from(stagesToRetrigger));
                  toast.success(`已重置 ${res.data?.reset ?? 0} 個步驟（${res.data?.task_count ?? 0} 個任務）`);
                  queryClient.invalidateQueries({ queryKey: ['ali-pd-panel', aliPdPanel.keyword] });
                } catch (err: unknown) {
                  toast.error(`補跑失敗: ${err instanceof Error ? err.message : String(err)}`);
                } finally {
                  setIsRetriggeringAli(false);
                }
              }}
              className="px-2.5 py-1 rounded text-xs bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50">
              {isRetriggeringAli ? '補跑中…' : `補跑${aliPdFilter !== 'all' ? `（${aliPdFilter === 'failed' ? '失敗' : aliPdFilter === 'pending' ? '等待' : '未開始'}）` : ''}`}
            </button>
          </div>

          {/* Body — 篩選後的 URL 列表 */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {aliPdProductsFetching && <div className="text-center py-8 text-muted-foreground text-sm">載入中…</div>}
            {(() => {
              const filtered = (aliPdProductsData?.data ?? []).filter(item => {
                if (aliPdFilter === 'all') return true;
                if (aliPdFilter === 'failed') return item.task_status === 'failed' || item.parse_status === 'failed';
                if (aliPdFilter === 'pending') return item.task_status === 'pending' || item.task_status === 'running';
                return !item.task_status;
              });
              return filtered.map((item, idx) => {
                return (
                  <div key={item.id ?? item.url} className="border rounded p-2.5 text-xs space-y-1.5">
                    {/* Title + URL row */}
                    <div className="flex items-start gap-1.5">
                      <span className="text-muted-foreground shrink-0">#{aliPdOffset + idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate" title={item.title ?? ''}>
                          {item.title ?? <span className="text-muted-foreground italic">{item.url ?? 'URL 未知'}</span>}
                        </div>
                        {item.company && (
                          <div className="text-[10px] text-muted-foreground truncate">{item.company}</div>
                        )}
                      </div>
                    </div>

                    {/* Pipeline stage chips — from alibaba_product_profiles (correct source) */}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {[
                        { key: 'BA',     done: item.task_status === 'completed' },
                        { key: 'iframe', done: item.has_iframe },
                        { key: 'imgs',   done: (item.iframe_image_count ?? 0) > 0 },
                        { key: 'scrn',   done: item.has_screenshot },
                        { key: 'md',     done: item.has_markdown },
                        { key: 'smi',    done: item.smi_done },
                      ].map(({ key, done }) => (
                        <span key={key}
                          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded font-mono text-[10px] ${done ? 'bg-green-100 text-green-700' : 'bg-muted/50 text-muted-foreground'}`}>
                          {done ? '✅' : '○'} <span>{key}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Footer — 分頁 + iframe 補跑 */}
          <div className="px-4 py-2 border-t bg-muted/20 text-xs flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                共 {aliPdProductsData?.total_products ?? 0} 筆 URL
                {aliPdFilter !== 'all' && <span className="ml-1">（篩選中）</span>}
              </span>
              <button
                disabled={isIframeFetching}
                onClick={async () => {
                  setIsIframeFetching(true)
                  try {
                    await alibabaApi.triggerIframeFetch()
                    toast.success('Batch C-2 iframe fetch 已啟動（背景執行）')
                  } catch { toast.error('啟動 iframe fetch 失敗') }
                  finally { setIsIframeFetching(false) }
                }}
                className="px-2 py-0.5 rounded border text-[10px] bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 disabled:opacity-50 whitespace-nowrap"
              >
                {isIframeFetching ? '啟動中…' : '↺ 補跑 iframe（全批）'}
              </button>
              <button
                disabled={isBackfillingC4}
                onClick={async () => {
                  setIsBackfillingC4(true)
                  try {
                    const res = await alibabaApi.backfillC4(10)
                    toast.success(`C-4 補觸發：${res.data.triggered} 筆`)
                  } catch { toast.error('補觸發 C-4 失敗') }
                  finally { setIsBackfillingC4(false) }
                }}
                className="px-2 py-0.5 rounded border text-[10px] bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 disabled:opacity-50 whitespace-nowrap"
              >
                {isBackfillingC4 ? '觸發中…' : '↺ 補觸發 C-4 截圖（10 筆）'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={aliPdOffset === 0}
                onClick={() => setAliPdOffset(Math.max(0, aliPdOffset - ALI_PD_LIMIT))}
                className="px-2 py-0.5 rounded bg-muted disabled:opacity-40">◀</button>
              <span className="font-mono">
                {Math.floor(aliPdOffset / ALI_PD_LIMIT) + 1} / {Math.max(1, Math.ceil((aliPdProductsData?.total_products ?? 0) / ALI_PD_LIMIT))}
              </span>
              <button
                disabled={(aliPdOffset + ALI_PD_LIMIT) >= (aliPdProductsData?.total_products ?? 0)}
                onClick={() => setAliPdOffset(aliPdOffset + ALI_PD_LIMIT)}
                className="px-2 py-0.5 rounded bg-muted disabled:opacity-40">▶</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── SC Drill-down Panel — 已移至 SC table tbody inline 展開 ─── */}
      {false && scDrilldown && (
        <div className="fixed inset-y-0 right-0 w-[480px] bg-background border-l shadow-xl z-50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div>
              <div className="font-semibold text-sm truncate max-w-[360px]">{scDrilldown.keyword}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                SC — 分類頁分頁詳情
                {scDrilldownData && (
                  <span className="ml-1 text-blue-600">（{scDrilldownData.data.length} 頁）</span>
                )}
              </div>
            </div>
            <button
              onClick={() => setScDrilldown(null)}
              className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground">✕</button>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {scDrilldownFetching && <div className="text-center py-8 text-sm text-muted-foreground">載入中…</div>}
            {!scDrilldownFetching && scDrilldownData && (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-2 py-1.5 font-medium">第幾頁</th>
                    <th className="px-2 py-1.5 text-center font-medium">狀態</th>
                    <th className="px-2 py-1.5 text-center font-medium text-green-600">產品數</th>
                    <th className="px-2 py-1.5 text-center font-medium text-purple-600">公司數</th>
                    <th className="px-2 py-1.5 text-center font-medium text-muted-foreground">更新</th>
                  </tr>
                </thead>
                <tbody>
                  {scDrilldownData.data.map(row => {
                    const statusIcon =
                      row.status === 'completed' ? '✅'
                      : row.status === 'failed'    ? '❌'
                      : row.status === 'running'   ? '⏳'
                      : row.status === 'pending'   ? '○'
                      : '—'
                    return (
                      <tr key={row.search_id} className="border-b hover:bg-accent/20">
                        <td className="px-2 py-1.5 font-mono font-semibold">P{row.page_number}</td>
                        <td className="px-2 py-1.5 text-center">{statusIcon}</td>
                        <td className="px-2 py-1.5 text-center font-mono text-green-600">{row.product_count}</td>
                        <td className="px-2 py-1.5 text-center font-mono text-purple-600">{row.company_url_count}</td>
                        <td className="px-2 py-1.5 text-center text-muted-foreground">
                          {row.updated_at ? new Date(row.updated_at).toLocaleDateString('zh-TW') : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* SC 補跑按鈕 */}
          <div className="border-t p-3 flex gap-2 bg-muted/10">
            {(() => {
              const failedCount = scDrilldownData?.data.filter(r => r.status === 'failed').length ?? 0
              const pendingCount = scDrilldownData?.data.filter(r => r.status === 'pending').length ?? 0
              const isBusy = scRetryingKws.has(scDrilldown.keyword)
              const doRetry = async (mode: 'failed' | 'pending') => {
                setScRetryingKws(prev => new Set(prev).add(scDrilldown.keyword))
                try {
                  const res = await alibabaApi.retryScPages(scDrilldown.keyword, mode)
                  toast.success(`補跑${mode === 'failed' ? '失敗' : '待跑'}頁：${res.data.data.retried} 筆已加入佇列`)
                  queryClient.invalidateQueries({ queryKey: ['ali-sc-pages', scDrilldown.keyword] })
                  queryClient.invalidateQueries({ queryKey: ['ali-search-progress'] })
                } catch { toast.error('補跑失敗') }
                finally { setScRetryingKws(prev => { const s = new Set(prev); s.delete(scDrilldown.keyword); return s; }) }
              }
              return (
                <>
                  <button
                    disabled={isBusy || failedCount === 0}
                    onClick={() => doRetry('failed')}
                    className="flex-1 text-xs py-1.5 px-3 rounded border hover:bg-muted disabled:opacity-50"
                  >
                    {isBusy ? '補跑中…' : `↺ 補跑失敗頁（${failedCount}）`}
                  </button>
                  <button
                    disabled={isBusy || pendingCount === 0}
                    onClick={() => doRetry('pending')}
                    className="flex-1 text-xs py-1.5 px-3 rounded border hover:bg-muted disabled:opacity-50"
                  >
                    {isBusy ? '補跑中…' : `↺ 補跑待爬頁（${pendingCount}）`}
                  </button>
                </>
              )
            })()}
          </div>

          {/* SC Suppliers 統計 */}
          {scSuppliersStats && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-b bg-muted/30">
              Suppliers: {scSuppliersStats.crawled}/{scSuppliersStats.total_suppliers} 已爬
              {scSuppliersStats.country_count > 0 && ` | ${scSuppliersStats.country_count} 國`}
              {scSuppliersStats.avg_rating > 0 && ` | 均評 ${scSuppliersStats.avg_rating}`}
            </div>
          )}

          {/* SC Suppliers 補爬按鈕 */}
          <div className="border-t p-3">
            <button
              className="w-full text-xs py-1.5 px-3 rounded border hover:bg-muted disabled:opacity-50"
              onClick={async () => {
                try {
                  const res = await alibabaApi.batchCrawlSuppliers(scDrilldown!.keyword, 5)
                  alert(`Suppliers 補爬：${res.data.pages_crawled} 頁，${res.data.total_cards} 家供應商`)
                  queryClient.invalidateQueries({ queryKey: ['ali-sc-pages', scDrilldown!.keyword] })
                } catch { alert('Suppliers 補爬失敗') }
              }}
            >
              ↺ 補爬 Suppliers Tab（最多 5 頁）
            </button>
          </div>
        </div>
      )}

      {/* ─── CO Drill-down Panel — 已移至 CO table tbody inline 展開 ─── */}
      {false && coDrilldown && (
        <div className="fixed inset-y-0 right-0 w-[480px] bg-background border-l shadow-xl z-50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div>
              <div className="font-semibold text-sm truncate max-w-[360px]">{coDrilldown.keyword}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                CO — 公司清單
                {coDrilldownData && (
                  <>
                    <span className="ml-1 text-blue-600">（{coDrilldownData.data.length} 家）</span>
                    {!coDrilldownData.table_exists && (
                      <span className="ml-2 text-yellow-600 bg-yellow-50 px-1.5 rounded">Crawler 未建，僅顯示 URL</span>
                    )}
                  </>
                )}
              </div>
            </div>
            <button
              onClick={() => setCoDrilldown(null)}
              className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground">✕</button>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {coDrilldownFetching && <div className="text-center py-8 text-sm text-muted-foreground">載入中…</div>}
            {!coDrilldownFetching && coDrilldownData && (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-2 py-1.5 font-medium min-w-[160px]">公司名稱</th>
                    <th className="px-2 py-1.5 text-center font-medium">Profile</th>
                    <th className="px-2 py-1.5 text-center font-medium">Nav</th>
                    <th className="px-2 py-1.5 text-center font-medium text-muted-foreground">更新</th>
                  </tr>
                </thead>
                <tbody>
                  {coDrilldownData.data.map(row => {
                    const profileIcon =
                      row.profile_status === 'completed' ? '✅'
                      : row.profile_status === 'failed'   ? '❌'
                      : row.profile_status === 'running'  ? '⏳'
                      : row.profile_status               ? '○'
                      : '—'
                    return (
                      <tr key={row.company_url} className="border-b hover:bg-accent/20">
                        <td className="px-2 py-1.5">
                          <div className="font-medium truncate max-w-[160px]" title={row.company_name ?? ''}>
                            {row.company_name ?? <span className="text-muted-foreground italic">未知</span>}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate max-w-[160px]" title={row.company_url}>
                            {row.company_url}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-center">{profileIcon}</td>
                        <td className="px-2 py-1.5 text-center">
                          {row.nav_parsed ? (
                            <span>✅{row.nav_category_count > 0 && <span className="ml-1 text-xs text-blue-600">📁 {row.nav_category_count} 類</span>}</span>
                          ) : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-center text-muted-foreground">
                          {row.updated_at ? new Date(row.updated_at).toLocaleDateString('zh-TW') : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* CO 補爬按鈕 */}
          <div className="border-t p-3 bg-muted/10">
            {(() => {
              const pendingCount = coDrilldownData?.data.filter(r => r.profile_status !== 'completed').length ?? 0
              const tableExists = coDrilldownData?.table_exists ?? false
              const isBusy = coRetryingKws.has(coDrilldown.keyword)
              return (
                <>
                <button
                  disabled={isBusy}
                  onClick={async () => {
                    setCoRetryingKws(prev => new Set(prev).add(coDrilldown.keyword))
                    try {
                      const limit = Math.min(pendingCount || 20, 20)
                      const res = await alibabaApi.batchCrawlCompanies(coDrilldown.keyword, limit)
                      toast.success(`補爬公司 Profile：${res.data.queued} 家已排入佇列`)
                      queryClient.invalidateQueries({ queryKey: ['ali-co-companies', coDrilldown.keyword] })
                      queryClient.invalidateQueries({ queryKey: ['ali-company-progress'] })
                    } catch { toast.error('補爬失敗') }
                    finally { setCoRetryingKws(prev => { const s = new Set(prev); s.delete(coDrilldown.keyword); return s; }) }
                  }}
                  className="w-full text-xs py-1.5 px-3 rounded border hover:bg-muted disabled:opacity-50"
                >
                  {isBusy
                    ? '排入佇列中…'
                    : tableExists
                      ? `↺ 補爬公司 Profile（最多 20 家，共 ${pendingCount} 待爬）`
                      : '↺ 補爬公司 Profile（建表後才能用）'
                  }
                </button>
                <button
                  className="w-full text-xs py-1.5 px-3 rounded border hover:bg-muted mt-1"
                  onClick={async () => {
                    try {
                      const res = await alibabaApi.batchCrawlCoProducts(coDrilldown!.keyword, 5)
                      toast.success(`補爬產品列表：${res.data.crawled} 家，${res.data.total_products} 筆`)
                    } catch { toast.error('補爬產品列表失敗') }
                  }}
                >
                  ↺ 補爬公司產品列表（5 家）
                </button>
                <button
                  className="w-full text-xs py-1.5 px-3 rounded border hover:bg-muted mt-1 border-orange-300"
                  onClick={async () => {
                    try {
                      const res = await alibabaApi.batchBaCrawlCompanies(coDrilldown!.keyword, 3)
                      toast.success(`BA 爬廠區資訊：${res.data.crawled} 家完成`)
                    } catch { toast.error('BA 爬取失敗') }
                  }}
                >
                  ↺ BA 補爬廠區資訊（3 家，消耗 BA Credits）
                </button>
                </>
              )
            })()}
          </div>
        </div>
      )}

      {/* Tab2 A4 Step Panel — 點擊關鍵字列後顯示 Step1~7 進度 */}
      {selectedA4Keyword && (matrixMode === 'serp-llm' || matrixMode === 'all') && (() => {
        const sa = serpAnalysisMap.get(selectedA4Keyword.keyword)
        const steps = a4StepsData?.steps ?? []
        const totalCost = steps.reduce((s, st) => s + (st.cost ?? 0), 0)
        const totalTokensIn  = steps.reduce((s, st) => s + (st.tokens_input  ?? 0), 0)
        const totalTokensOut = steps.reduce((s, st) => s + (st.tokens_output ?? 0), 0)

        const toggleStep = (step: number) => setExpandedSteps(prev => {
          const next = new Set(prev)
          next.has(step) ? next.delete(step) : next.add(step)
          return next
        })

        const STEP_LABELS: Record<number, string> = {
          1: 'DataForSEO SERP',
          2: 'Ngram 分析',
          3: '競品分析',
          4: '使用者意圖',
          5: '機會分析',
          6: '內容綱要',
          7: 'H標籤分組',
        }

        return (
          <div className="fixed inset-y-0 right-0 w-[500px] bg-background border-l shadow-xl z-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
              <div>
                <div className="font-semibold text-sm truncate max-w-[370px]">{selectedA4Keyword.keyword}</div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                  <span>SERP A4 分析進度</span>
                  {totalCost > 0 && <span className="text-amber-600 font-mono">💰 ${(totalCost/100).toFixed(3)}</span>}
                  {(totalTokensIn > 0 || totalTokensOut > 0) && (
                    <span className="text-muted-foreground font-mono">
                      ↑{totalTokensIn.toLocaleString()} ↓{totalTokensOut.toLocaleString()} tokens
                    </span>
                  )}
                  {a4Fetching && <span className="animate-pulse">更新中…</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {sa && (
                  <button
                    onClick={() => navigate(`/analysis-reports/${sa.request_id}`)}
                    className="text-xs px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90">
                    完整報告 →
                  </button>
                )}
                <button onClick={() => setSelectedA4Keyword(null)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground">✕</button>
              </div>
            </div>

            {/* H標籤警示 */}
            {sa && !sa.analysis_ready && (
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between">
                <span>⚠️ H標籤資料不足（{sa.parsed_htags}/10 筆），建議先補資料</span>
                <button
                  onClick={() => navigate(`/pipeline-monitor?tab=serp`)}
                  className="text-xs px-2 py-0.5 rounded bg-amber-200 hover:bg-amber-300">
                  → SERP A1~A3
                </button>
              </div>
            )}

            {/* Steps list */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {steps.length === 0 && !a4Fetching && (
                <div className="text-center py-8 text-muted-foreground text-sm">尚無分析資料</div>
              )}
              {steps.map((step) => {
                const statusColor = step.status === 'completed' ? 'text-green-600'
                  : step.status === 'failed' ? 'text-red-500'
                  : step.status === 'running' ? 'text-blue-500'
                  : 'text-muted-foreground'
                const statusIcon = step.status === 'completed' ? '✅'
                  : step.status === 'failed' ? '❌'
                  : step.status === 'running' ? '⏳'
                  : '○'

                return (
                  <div key={step.step} className="border rounded p-2.5 text-xs space-y-1.5">
                    {/* Step header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-muted-foreground w-6">S{step.step}</span>
                        <span className="font-medium">{STEP_LABELS[step.step] ?? step.step_name}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`font-medium ${statusColor}`}>{statusIcon} {step.status}</span>
                        {(step.output_markdown || step.output_json) && (
                          <button
                            onClick={() => toggleStep(step.step)}
                            className="text-[11px] text-blue-600 hover:underline ml-1">
                            {expandedSteps.has(step.step) ? '收合 ▲' : '查看輸出 ▼'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Meta row */}
                    {(step.ai_model || step.duration_ms || step.cost) && (
                      <div className="flex items-center gap-3 text-muted-foreground text-[11px] pl-8">
                        {step.ai_model && <span>🤖 {step.ai_model.split('/').pop()}</span>}
                        {step.duration_ms && <span>⏱ {(step.duration_ms/1000).toFixed(1)}s</span>}
                        {step.cost && <span>💰 ${(step.cost/100).toFixed(3)}</span>}
                        {step.tokens_input && <span>↑{step.tokens_input} ↓{step.tokens_output}</span>}
                      </div>
                    )}

                    {/* Step 1 特有：SERP 結果數 */}
                    {step.step === 1 && step.serp_results_count !== undefined && (
                      <div className="pl-8 text-[11px] text-muted-foreground">
                        SERP 結果：{step.serp_results_count} 筆
                      </div>
                    )}

                    {/* Step 2 特有：Ngram URL 數 */}
                    {step.step === 2 && step.ngram_url_count !== undefined && (
                      <div className="pl-8 text-[11px] text-muted-foreground">
                        分析 URL：{step.ngram_url_count} 筆
                      </div>
                    )}

                    {/* input_summary */}
                    {step.input_summary && (
                      <div className="pl-8 text-[11px] text-muted-foreground bg-muted/30 rounded px-2 py-1">
                        <span className="text-muted-foreground/70">輸入摘要：</span>{step.input_summary.slice(0, 120)}{step.input_summary.length > 120 ? '…' : ''}
                      </div>
                    )}

                    {/* error */}
                    {step.error_message && (
                      <div className="pl-8 text-[11px] text-red-600 bg-red-50 rounded px-2 py-1">
                        {step.error_message.slice(0, 200)}
                      </div>
                    )}

                    {/* Expandable output — output_markdown / output_json */}
                    {expandedSteps.has(step.step) && (
                      <div className="pl-8 mt-1.5 border-t pt-2">
                        {step.output_markdown && (
                          <div className="text-[11px] bg-muted/20 rounded p-2 max-h-[300px] overflow-y-auto">
                            <div className="text-muted-foreground mb-1 text-[10px] uppercase tracking-wide">AI 輸出（Markdown）</div>
                            <pre className="whitespace-pre-wrap font-sans text-[11px] leading-relaxed">
                              {step.output_markdown}
                            </pre>
                          </div>
                        )}
                        {step.output_json && !step.output_markdown && (
                          <div className="text-[11px] bg-muted/20 rounded p-2 max-h-[300px] overflow-y-auto">
                            <div className="text-muted-foreground mb-1 text-[10px] uppercase tracking-wide">AI 輸出（JSON）</div>
                            <pre className="whitespace-pre-wrap font-mono text-[10px]">
                              {JSON.stringify(step.output_json, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t bg-muted/20 text-xs text-muted-foreground flex justify-between">
              <span>自動刷新 30s</span>
              {sa?.completed_at && <span>完成：{new Date(sa.completed_at).toLocaleString('zh-TW')}</span>}
            </div>
          </div>
        )
      })()}

      {/* Hover tooltip — URL list with blocklist + failure info */}
      {hoverCell && (
        <div
          className="fixed z-50 bg-white border border-gray-200 shadow-2xl rounded-lg p-3 text-xs w-[420px] max-h-96 overflow-y-auto pointer-events-auto"
          style={{
            left: Math.min(hoverCell.x + 10, window.innerWidth - 440),
            top: Math.max(hoverCell.y - 10, 10),
          }}
          onMouseEnter={() => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); }}
          onMouseLeave={() => { hoverTimerRef.current = setTimeout(() => setHoverCell(null), 150); }}
        >
          <div className="font-semibold text-[11px] text-gray-500 mb-2 pb-1 border-b flex items-center justify-between">
            <span>{hoverCell.keyword}</span>
            <span className="text-blue-600 font-mono">{hoverCell.stage.toUpperCase()}</span>
          </div>
          {hoverLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : hoverItems.length === 0 ? (
            <div className="text-center text-gray-400 py-4">無資料</div>
          ) : (
            <div className="space-y-0.5">
              {/* TOP 20 first */}
              {hoverItems.filter(i => i.rank_group === 'top').length > 0 && (
                <div className="text-[10px] text-gray-400 font-medium mb-1">TOP 20</div>
              )}
              {hoverItems.filter(i => i.rank_group === 'top').map(item => (
                <div key={item.url} className="flex items-center gap-1.5 py-0.5 border-b border-gray-50 last:border-0">
                  <span className="text-[10px] text-gray-400 w-5 text-right shrink-0">{item.rank_absolute}</span>
                  {item.is_blocklisted ? (
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium shrink-0">🚫黑名單</span>
                  ) : item.stage_done ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                  ) : item.task_status === 'processing' ? (
                    <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin shrink-0" />
                  ) : item.task_status === 'pending' ? (
                    <Clock className="h-3.5 w-3.5 text-orange-400 shrink-0" />
                  ) : item.task_status === 'failed' ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                  ) : (
                    <span className="h-3.5 w-3.5 rounded-full border border-gray-300 shrink-0 inline-block" />
                  )}
                  <span className="truncate text-gray-600 font-mono flex-1 min-w-0" title={item.url}>{item.domain}</span>
                  {item.blocklist_status === 'active'
                    ? <span className="text-red-500 text-[10px] shrink-0">🚫 封鎖中</span>
                    : item.blocklist_status === 'historical'
                    ? <span className="text-orange-400 text-[10px] shrink-0">⚠️ 曾封鎖</span>
                    : item.stage_done
                    ? <span className="text-green-500 text-[10px] shrink-0">✓</span>
                    : item.scraping_task_id
                    ? <span className={`text-[10px] shrink-0 ${
                        item.task_status === 'failed' ? 'text-red-400' :
                        item.task_status === 'processing' ? 'text-blue-400' : 'text-gray-400'
                      }`}>{item.task_status ?? '待處理'}</span>
                    : <span className="text-amber-500 text-[10px] shrink-0 font-medium" title="欠: 抓取任務未建立 — 點「立刻處理」自動建立">⚡ 欠任務</span>
                  }
                </div>
              ))}
              {/* Non-TOP summary */}
              {hoverItems.filter(i => i.rank_group === 'non_top').length > 0 && (
                <div className="mt-2 pt-1 border-t text-[10px] text-gray-400">
                  非TOP {hoverItems.filter(i => i.rank_group === 'non_top').length} 筆 —
                  完成 {hoverItems.filter(i => i.rank_group === 'non_top' && i.stage_done).length} ／
                  黑名單 {hoverItems.filter(i => i.rank_group === 'non_top' && i.is_blocklisted).length}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab1 SERP Keyword Panel — click keyword row to expand URL × A1/A2/A3 status */}
      {selectedSerpKeyword && matrixMode === 'serp' && (() => {
        // Merge three stage datasets by URL key
        const urlMap = new Map<string, {
          url: string; domain: string; rank_absolute: number; rank_group: string
          a1: { html_ssr_done: boolean; h_structure_done: boolean; markdown_done: boolean; nav_tree_done: boolean } | null
          a2: { schema_done: boolean } | null
          a3: { images_count: number } | null
          task_status: string
          is_blocklisted: boolean
        }>()

        // Base from A1
        for (const item of (serpPanelA1 as { data?: UrlDetailItem[] } | null)?.data ?? []) {
          urlMap.set(item.url, {
            url: item.url, domain: item.domain,
            rank_absolute: item.rank_absolute, rank_group: item.rank_group,
            a1: { html_ssr_done: item.html_ssr_done, h_structure_done: item.h_structure_done, markdown_done: item.markdown_done, nav_tree_done: item.nav_tree_done },
            a2: null, a3: null,
            task_status: item.task_status ?? '', is_blocklisted: item.is_blocklisted
          })
        }
        // Merge A2 schema
        for (const item of (serpPanelA2 as { data?: UrlDetailItem[] } | null)?.data ?? []) {
          if (urlMap.has(item.url)) urlMap.get(item.url)!.a2 = { schema_done: item.schema_done }
          else urlMap.set(item.url, { url: item.url, domain: item.domain, rank_absolute: item.rank_absolute, rank_group: item.rank_group, a1: null, a2: { schema_done: item.schema_done }, a3: null, task_status: item.task_status ?? '', is_blocklisted: item.is_blocklisted })
        }
        // Merge A3 images
        for (const item of (serpPanelA3 as { data?: UrlDetailItem[] } | null)?.data ?? []) {
          if (urlMap.has(item.url)) urlMap.get(item.url)!.a3 = { images_count: item.images_count }
          else urlMap.set(item.url, { url: item.url, domain: item.domain, rank_absolute: item.rank_absolute, rank_group: item.rank_group, a1: null, a2: null, a3: { images_count: item.images_count }, task_status: item.task_status ?? '', is_blocklisted: item.is_blocklisted })
        }

        const allItems = [...urlMap.values()].sort((a, b) => a.rank_absolute - b.rank_absolute)
        const topItems = allItems.filter(i => i.rank_group === 'top')
        const a1Done = topItems.filter(i => i.a1?.html_ssr_done && i.a1?.h_structure_done && i.a1?.markdown_done && i.a1?.nav_tree_done).length

        return (
          <div className="fixed inset-y-0 right-0 w-[520px] bg-background border-l shadow-xl z-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
              <div>
                <div className="font-semibold text-sm truncate max-w-[380px]">{selectedSerpKeyword}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  SERP 爬取進度 — TOP20 A-1 完成：
                  <span className={`ml-1 font-mono font-bold ${a1Done >= 16 ? 'text-green-600' : a1Done >= 10 ? 'text-yellow-600' : 'text-red-500'}`}>
                    {a1Done}/{topItems.length}
                  </span>
                  {fetchingA1 && <span className="ml-2 text-muted-foreground animate-pulse">更新中…</span>}
                </div>
              </div>
              <button onClick={() => setSelectedSerpKeyword(null)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground">✕</button>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                  <tr>
                    <th className="px-2 py-1.5 text-left w-8">#</th>
                    <th className="px-2 py-1.5 text-left">Domain / URL</th>
                    <th className="px-1 py-1.5 text-center w-20">A-1</th>
                    <th className="px-1 py-1.5 text-center w-10">A-2</th>
                    <th className="px-1 py-1.5 text-center w-12">A-3</th>
                    <th className="px-1 py-1.5 text-center w-12">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.map(item => {
                    const a1ok = item.a1?.html_ssr_done && item.a1?.h_structure_done && item.a1?.markdown_done && item.a1?.nav_tree_done
                    return (
                      <tr key={item.url} className={`border-b hover:bg-muted/30 ${item.rank_group === 'top' ? '' : 'opacity-60'}`}>
                        <td className="px-2 py-1 text-muted-foreground font-mono">{item.rank_absolute}</td>
                        <td className="px-2 py-1 max-w-[200px]">
                          <div className="truncate font-medium">{item.domain}</div>
                          <div className="truncate text-muted-foreground text-[10px]">{item.url}</div>
                        </td>
                        <td className="px-1 py-1 text-center">
                          {item.a1 ? (
                            <span className={`font-mono text-[10px] ${a1ok ? 'text-green-600' : 'text-yellow-600'}`}>
                              {item.a1.html_ssr_done ? 'H' : '○'}
                              {item.a1.h_structure_done ? 'S' : '○'}
                              {item.a1.markdown_done ? 'M' : '○'}
                              {item.a1.nav_tree_done ? 'N' : '○'}
                            </span>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-1 py-1 text-center">
                          {item.a2 ? (item.a2.schema_done ? '✅' : '○') : '—'}
                        </td>
                        <td className="px-1 py-1 text-center font-mono">
                          {item.a3 ? (
                            <span className={item.a3.images_count >= 100 ? 'text-green-600' : 'text-yellow-600'}>
                              {item.a3.images_count}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-1 py-1 text-center text-[10px]">
                          {item.is_blocklisted ? <span className="text-orange-500">🚫</span>
                            : item.task_status === 'completed' ? <span className="text-green-600">✅</span>
                            : item.task_status === 'failed' ? <span className="text-red-500">❌</span>
                            : <span className="text-muted-foreground">⏳</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t bg-muted/20 text-xs text-muted-foreground flex justify-between">
              <span>TOP20：{topItems.length} 筆 ｜ 全部：{allItems.length} 筆</span>
              <span>A-1 圖示：H=html_ssr S=h_structure M=markdown N=nav</span>
            </div>
          </div>
        )
      })()}
    </div>
  );
}
